// ClassicLayout — MVP の唯一の画面構成 (architecture.md §16).
//
// Layout (Phase 6.7 R16 で左カラムを 140px → 80px に細幅化):
//   ┌──────────┬─────────────────────────────────────┐
//   │ left     │            canvas (col-span-2)      │  ← 上段
//   │ (80px)   │                                     │
//   │ row-span ├──────────────────────────┬──────────┤
//   │   = 2    │  chat                    │ members  │  ← 下段 (同高 180px)
//   │          │  (1fr)                   │ (140px)  │
//   └──────────┴──────────────────────────┴──────────┘
//
// 旧版 `OekakiChatJava/screenshot/room.png` 観察:
//   - 左サイドバー上段: 白背景の 1 つの outset frame に 16 色 + ペン種 + ペン幅 +
//     機能ボタン群がまとめて入る (Phase 6.7-B-fix Item 4)。
//   - その下に ARGB スライダ (縦バー 4 本) → 別エリア。
//   - さらに下に MASK 領域 (Phase 6.7 R12 で 2 slot + 下部 toggle に変更, 旧
//     `MaskSetCanvas.java` 完全踏襲) → 別エリア。
//   - 中央: Canvas が右端まで伸び、その下に full-width (chat) + 右端小窓 (member)
//     の 2 ペインが同高で並ぶ。
//
// Phase 6 Task 6.6-B: 「設定」ボタンを左カラムに追加. RoomSettingsModal を
// トグルする (admin_token の有無は modal 側でガード).
//
// Phase 6.7-A Task R3 (旧): 3 列分割比 (180px / flex / 140px)、右カラム 2 段組
// (上空白 + 下 member 窓 240px)。R14 で grid 構造ごと再構成して廃止。
//
// Phase 6.7-B-fix Item 4: 左サイドバーを 3 領域に再構成
//   1. 白背景 outset frame ボックス: PalettePanel + ToolPanel
//   2. ARGB スライダ領域: ArgbSliderPanel
//   3. MASK 領域: MaskPanel
// 設定 / 退室 / ルーム名は最下段に配置。
//
// Phase 6.7-B-fix-2A:
// - 左カラム幅 180px → 140px (旧版実機相当 / 両サイドカラム同幅で見た目バランス改善)。
//   140px には 16 色 2x8 + ペン種 2x2 + 機能 GIF (横長 ~30px) + ARGB + MASK 3 値 +
//   マスク色 8 列が収まることを目視確認。
// - 退室ボタン色を --color-error (赤) → --color-bg-soft (青系, 設定ボタンと同色) に変更。
//   旧版でも退室は普通のボタン色で警告色ではないため。
//
// Phase 6.7 R16 (consult 2026-05-05): 左カラム 140px → 80px (旧版 room.png 実機相当).
// - 旧版は 50px 強の極細サイドバー (ARGB 50px Canvas + 16 色 2x8 で実質 50-60px).
//   新版で R14 まで 140px に拡張していたのを、本来の細さに戻す.
// - 80px 内訳: p-2 (8x2=16px) を引いて内寸 64px. 16 色 2x8 / ペン種 縦 2 行 /
//   ペン幅 cell 横 2 (各 ~28px) / ARGB Canvas 50px / MASK slot 24px x 2 / 機能 GIF
//   24px はいずれも 64px 以内に収まる.
// - ペン種ボタンは 80px 幅では「普通」「透過」を横 2 並びにすると窮屈なので
//   `grid-cols-1` (縦 1 列 2 行) に変更 (R16-4).
// - 設定 / 退室 / ルーム名は `text-[10px]` で 80px 内に収める.
// - PenWidthSlider の BAR_WIDTH を 116px → 60px に縮小 (左サイドバー追従).
//
// Phase 6.7 R14 (consult 2026-05-05): 2 行 x 3 列 grid 再構成。
//   - 旧 `grid-cols-[140px_1fr_140px]` の 1 行 grid (中央列が縦に Canvas+Chat、
//     右列が縦に空白+Member 240px) → `grid-cols-[140px_1fr_140px] grid-rows-[1fr_180px]`
//     の 2 行 grid に。
//   - 左カラムは `row-span-2`. 中央上段 = Canvas が `col-span-2` で右端まで延伸.
//     中央下段 = ChatPanel (1fr) / 右下段 = MemberPanel (140px) が同 row で同高 180px.
//   - 旧版 right-column 上半分の空白を廃止し、Canvas の横幅を最大化 (旧版 room.png 踏襲)。
//   - MemberPanel ラッパーの `bg-[var(--color-panel)]/80` 等の背景は撤去:
//     Stage 1 R13 で MemberPanel 自体が `bg-white` を持つので親背景は出ない。

import type { TexturePreset } from '@oekaki/protocol';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  type BellOnState,
  type BellSoundAdapter,
  createBellOnRoominStore,
  createBrowserBellSound,
} from '../../../application/bellOnRoomin.js';
import type { RoomState } from '../../../domain/RoomState.js';
import {
  type ChatFontScale,
  chatFontScaleToEm,
  createChatFontScalePreferenceStore,
} from '../../../infrastructure/chatFontScalePreference.js';
// カーソル入口の「New!」バッジ seen フラグ (2026-09-19 追補、絵ログ/過去キャンバスとは別 key)。
import { createCustomCursorNewBadgeSeenStore } from '../../../infrastructure/customCursorNewBadgeSeen.js';
import { ZOOM_FIT_MIN, ZOOM_MAX } from '../../../infrastructure/drawing/CanvasDrawingEngine.js';
// 使い方ボタンの「New!」バッジ seen フラグ (2026-09-22 追補、目立たせ改修)。
import { createHelpButtonSeenStore } from '../../../infrastructure/helpButtonSeen.js';
import {
  CHAT_ROW_PX_MAX,
  CHAT_ROW_PX_MIN,
  clampChatRowPx,
  clampMemberColPx,
  createPaneSizePreferenceStore,
  MEMBER_COL_PX_MAX,
  MEMBER_COL_PX_MIN,
} from '../../../infrastructure/paneSizePreference.js';
// 過去キャンバス入口の「New!」バッジ seen フラグ (Slice 2、絵ログとは別 key)。
import { createPastCanvasSeenStore } from '../../../infrastructure/pastCanvasSeen.js';
import { createPreviewSizePreferenceStore } from '../../../infrastructure/previewSizePreference.js';
// T3 絵ログ (タイムラプス) 共有 (growth-timelapse-share-2026-07-02.md §5.3.8):
// Classic 上部バーにも入口を露出する。`?worker` import (timelapseWorkerFactory) は
// ここで解決してモーダルへ注入する (= Modern と同方針、node テスト可能に保つ)。
import { createTimelapseWorker } from '../../../infrastructure/timelapse/worker/timelapseWorkerFactory.js';
// 絵ログ共有ボタンの「New!」バッジ seen フラグ (consult 2026-07-08 案B): 初回に
// モーダルを開いたら消す。Classic / Modern 両入口で同じ store key を共有。
import { createTimelapseShareSeenStore } from '../../../infrastructure/timelapseShareSeen.js';
import {
  createUiScalePreferenceStore,
  defaultDetectIpadLike,
  rescueSidebarScale,
  type UiScale,
  uiScaleToCssZoom,
} from '../../../infrastructure/uiScalePreference.js';
import { ConfirmDialog } from '../../ConfirmDialog.js';
import { FeedbackButton } from '../../FeedbackButton.js';
import { TIMELAPSE_SHARE_ENABLED } from '../../featureFlags.js';
import { UNDO_REJECT_DISPLAY_MS, useConflictAwareUndo } from '../../hooks/useConflictAwareUndo.js';
import { useInviteDialog } from '../../hooks/useInviteDialog.js';
import { useSession } from '../../hooks/useSession.js';
import { useTimelapseExportSupported } from '../../hooks/useTimelapseExportSupported.js';
import { useWhatsNew } from '../../hooks/useWhatsNew.js';
import { InviteDialog } from '../../InviteDialog.js';
import { buildHelpUrl } from '../../manual/helpUrl.js';
import { PastCanvasListModal } from '../../PastCanvasListModal.js';
import { PenSettingsModal } from '../../PenSettingsModal.js';
import { useConnectionLocked } from '../../providers/ConnectionLockContext.js';
import { RoomSettingsModal } from '../../RoomSettingsModal.js';
import { ShortcutSettingsModal } from '../../ShortcutSettingsModal.js';
import { TimelapseShareModal } from '../../TimelapseShareModal.js';
import { UserPreferencesModal } from '../../UserPreferencesModal.js';
import { buildRoomInviteUrl } from '../../urlNavigation.js';
import { WhatsNewDialog } from '../../WhatsNewDialog.js';
import { stepPenWidth } from '../modern/widthPresets.js';
import { markCursorEditorHintSeen, readCursorEditorHintSeen } from '../shared/customCursorHint.js';
import {
  clampFloatPosition,
  FLOAT_EDGE_INSET_PX,
  type FloatPoint,
  floatPanelSide,
  floatPositionFromPointer,
  NO_SHELL_DRAG_SELECTOR,
} from '../shared/floatShellGeometry.js';
import { EDGE_SNAP_THRESHOLD_PX, resolveEdgeSnapSide } from '../shared/panelDragGeometry.js';
import { RoomHeader, type RoomHeaderItem } from '../shared/RoomHeader.js';
import { RoomRetentionChip } from '../shared/RoomRetentionChip.js';
import { useFullscreen } from '../shared/useFullscreen.js';
import { usePanelDragGrip } from '../shared/usePanelDragGrip.js';
import type { RoomChrome } from '../shared/useRoomChrome.js';
import { useLayoutKeyboardShortcuts } from '../useLayoutKeyboardShortcuts.js';
import { isTextInputFocus } from '../useSpaceHoldPan.js';
import { ArgbSliderPanel } from './ArgbSliderPanel.js';
import { isTouchAppleDevice } from './applePlatform.js';
import { ChatPanel, type ChatPanelHandle } from './ChatPanel.js';
import { ColorWheelPalette } from './ColorWheelPalette.js';
import { CursorEditorPopup } from './CursorEditorPopup.js';
import { CursorHeaderButton } from './CursorHeaderButton.js';
// S3 スライス2 (floating-panels-2026-08-01.md §7): パレットパネルの detach 状態
// (docked/floating) の pure helper。
import {
  type ClassicToolFloatPosition,
  createSidebarDockSideStore,
  createToolFloatOpenStore,
  defaultToolFloatPosition,
  readToolFloatPosition,
  type SidebarDockSide,
  TOOL_FLOAT_MARGIN,
  TOOL_FLOAT_NOMINAL_HEIGHT,
  TOOL_FLOAT_NOMINAL_WIDTH,
  writeToolFloatPosition,
} from './classicToolFloat.js';
import {
  type Floating2DResizeAnchor,
  type FloatingPopupPosition,
  type FloatingPopupSize,
  type FloatingResizeZone,
  nextFloatingResize,
} from './floatingChatPopup.js';
import { ImagepasteToastOverlay } from './ImagepasteToastOverlay.js';
import { LayerPopup } from './LayerPopup.js';
import { createLayerPopupVisibilityStore } from './layerPopup.js';
import { outsetFrame } from './legacyFrames.js';
import { MaskPanel } from './MaskPanel.js';
import { MemberPanel } from './MemberPanel.js';
import { PalettePanel } from './PalettePanel.js';
import { PenPopup } from './PenPopup.js';
import { PenPressureConfig } from './PenPressureConfig.js';
import { PreviewOverlay } from './PreviewOverlay.js';
import { createPenPopupVisibilityStore } from './penPopup.js';
import { isLocalInteractionActive } from './previewOverlay.js';
import { ToolPanel } from './ToolPanel.js';
// conflict-aware Undo (2026-07-23 是正、modern-layout.md §1.1 表現パリティ): Modern の
// 拒否トースト/bbox ハイライトと同じ共有部品を Classic からも直接描画する
// (UndoConflictHighlightOverlay 自体は元々 layouts/classic/ 配下、Modern から import
// されていた — 置き場所はそのまま、Classic からも呼ぶだけ)。
import { UndoConflictHighlightOverlay } from './UndoConflictHighlightOverlay.js';

export interface ClassicLayoutProps {
  readonly state: RoomState;
  /**
   * 2026-06-08 Modern Layout shell (docs/_wip/modern-layout.md §9.4.1 ③): Classic ⇄
   * Modern 切替トグル (置き場所 A = 両レイアウト共通 chrome)。「Classic 側にも同じ
   * トグル項目を出す」確定スコープに対応。App 側で `useLayoutPreference` を解決して
   * この slot に `<LayoutToggle>` を流し込む (= レイアウト解決ロジックは App に集約、
   * ClassicLayout は表示位置だけ提供)。スマホ (= Modern 固定でトグル非表示) の場合は
   * App が `undefined` を渡すので、Classic は従来どおり何も出さない (= 既存挙動温存)。
   */
  readonly layoutToggle?: React.ReactNode;
  /**
   * §9.7 engine 永続化 Phase 2 (2026-06-08): 描画状態 store + engine 接続。RoomLayout が
   * 所有して渡す (= 切替を跨いで永続)。canvas 本体・overlay・engine は CanvasStage が描画し、
   * ClassicLayout は **chrome ビュー** (パレット/ツール/マスク/筆圧/レイヤー等のパネル)
   * だけを担う。pen/color/mask/pressure/zoom/layer の状態は全てこの chrome 越しに読み書きする
   * (= ストアのビュー化)。Classic 固有の UI 状態 (ARGB mode / ColorWheel open / Preview open /
   * LayerPopup visibility / pan toggle / fullscreen / uiHidden / 個人 UI スケール / bell 等)
   * は ClassicLayout が引き続き自前で持つ (= レイアウト固有 concern)。
   */
  readonly chrome: RoomChrome;
  /**
   * §9.7: canvas overflow scroll の anchor (= プレビュー/pan が scroll 位置を読み書き)。
   * RoomLayout が所有し、CanvasStage の scroll コンテナと共有する。
   */
  readonly canvasScrollRef: React.RefObject<HTMLDivElement | null>;
  /**
   * §9.7: canvas 領域の「空スロット div」への ref。CanvasStage がこの矩形を計測して
   * absolute で重なる (= 見た目は今と同一、canvas 本体はここに無い)。
   */
  readonly canvasSlotRef: React.RefObject<HTMLDivElement | null>;
  /** 全消し確認モーダルの開閉 (RoomLayout が CanvasStage/chrome と共有)。 */
  readonly clearConfirmOpen: boolean;
  readonly setClearConfirmOpen: (open: boolean) => void;
  /**
   * 荒らし復旧 UI「?recover=<seq>」深リンク (vandalism-recovery-ui-2026-09-27.md
   * §2「入口を『続きを描く』に統一」)。RoomLayout が URL から読んで渡す。mount 時に
   * 値があれば過去キャンバス一覧モーダルを自動で開き (`PastCanvasListModal` の
   * `initialRecoverSeq` に橋渡し)、`onRecoverDeepLinkConsumed` で RoomLayout 側の
   * 状態をクリアしてもらう (= レイアウト切替 (remount) で再度自動オープンしない)。
   */
  readonly initialRecoverSeq?: number;
  readonly onRecoverDeepLinkConsumed?: () => void;
  /** 入室直後の「新機能の紹介」自動表示 (RoomLayout の useWhatsNewEntryAutoOpen が決める)。 */
  readonly whatsNewAutoOpen?: boolean;
  readonly onWhatsNewAutoOpened?: () => void;
}

// 2026-06-12: 上部バー (TOPBAR_PX / TopBarItem / TopBarScope / TopBarAction /
// TopBar / TopBarButton) は Classic / Modern 共通の `../shared/RoomHeader.tsx` へ
// 抽出した (= ドリフト防止のため構造的に 1 本化、ユーザ確定 2026-06-12)。帯高は
// 固定 28px から RoomHeader の自然高 (py-1.5) に変わったので TOPBAR_PX も廃止。
// 項目データの型は RoomHeaderItem を import して使う。

// ─── チャット detach 窓の枠ドラッグ (Windows 風 8 方向) リサイズゾーン ─────────
//
// PreviewOverlay の `PREVIEW_ZONES` と同じ意匠 (= 見えるグリップは出さず、パネル
// の縁に乗る透明な判定 div を 8 個 置き、ホバーで方向ごとの resize カーソルだけ
// 出す). 違いは「アスペクト固定の edge スカラ」ではなく「width/height 独立の
// 2D」を `nextFloatingResize` で動かす点だけ (= DOM パターンは完全に共通).
// 厚み / 隅サイズも preview と同値で揃える.
const CHAT_EDGE_ZONE_THICKNESS = 6;
const CHAT_CORNER_ZONE_SIZE = 12;
const CHAT_HALF_EDGE = CHAT_EDGE_ZONE_THICKNESS / 2;
// S3 スライス2 (floating-panels-2026-08-01.md §7、consult 2026-08-01): チャット
// detach 窓の drag 移動を共有 usePanelDragGrip + floatShellGeometry へ寄せる
// (= Slice 1 で他 4 popup に行ったのと同じ一点物解消、既存機能なので margin は
// 旧 clampPopupPosition の既定 8px を保って挙動を変えない)。
const CHAT_DETACH_EDGE_INSET_PX = 8;
// z: 辺=20 / 隅=21 (隅を辺より手前に重ねて対角リサイズを優先). n/ne/nw は
// title bar に重なるが、いずれも title bar より手前 (z 高) に置き
// `stopPropagation`/`preventDefault` で drag-move と分離する (= 「最上端の細い帯
// = リサイズ、その下のバー本体 = 移動」, Windows のウィンドウ枠を再現).
const DETACHED_CHAT_ZONES: ReadonlyArray<{
  readonly zone: FloatingResizeZone;
  readonly cursor: 'ns-resize' | 'ew-resize' | 'nesw-resize' | 'nwse-resize';
  readonly z: number;
  readonly style: React.CSSProperties;
}> = [
  // ── 辺ストリップ (z=20) ──
  {
    zone: 'n',
    cursor: 'ns-resize',
    z: 20,
    style: {
      top: -CHAT_HALF_EDGE,
      left: CHAT_CORNER_ZONE_SIZE,
      right: CHAT_CORNER_ZONE_SIZE,
      height: CHAT_EDGE_ZONE_THICKNESS,
    },
  },
  {
    zone: 's',
    cursor: 'ns-resize',
    z: 20,
    style: {
      bottom: -CHAT_HALF_EDGE,
      left: CHAT_CORNER_ZONE_SIZE,
      right: CHAT_CORNER_ZONE_SIZE,
      height: CHAT_EDGE_ZONE_THICKNESS,
    },
  },
  {
    zone: 'e',
    cursor: 'ew-resize',
    z: 20,
    style: {
      right: -CHAT_HALF_EDGE,
      top: CHAT_CORNER_ZONE_SIZE,
      bottom: CHAT_CORNER_ZONE_SIZE,
      width: CHAT_EDGE_ZONE_THICKNESS,
    },
  },
  {
    zone: 'w',
    cursor: 'ew-resize',
    z: 20,
    style: {
      left: -CHAT_HALF_EDGE,
      top: CHAT_CORNER_ZONE_SIZE,
      bottom: CHAT_CORNER_ZONE_SIZE,
      width: CHAT_EDGE_ZONE_THICKNESS,
    },
  },
  // ── 隅正方形 (z=21, 辺より手前) ──
  {
    zone: 'nw',
    cursor: 'nwse-resize',
    z: 21,
    style: {
      top: -CHAT_HALF_EDGE,
      left: -CHAT_HALF_EDGE,
      width: CHAT_CORNER_ZONE_SIZE,
      height: CHAT_CORNER_ZONE_SIZE,
    },
  },
  {
    zone: 'ne',
    cursor: 'nesw-resize',
    z: 21,
    style: {
      top: -CHAT_HALF_EDGE,
      right: -CHAT_HALF_EDGE,
      width: CHAT_CORNER_ZONE_SIZE,
      height: CHAT_CORNER_ZONE_SIZE,
    },
  },
  {
    zone: 'sw',
    cursor: 'nesw-resize',
    z: 21,
    style: {
      bottom: -CHAT_HALF_EDGE,
      left: -CHAT_HALF_EDGE,
      width: CHAT_CORNER_ZONE_SIZE,
      height: CHAT_CORNER_ZONE_SIZE,
    },
  },
  {
    zone: 'se',
    cursor: 'nwse-resize',
    z: 21,
    style: {
      bottom: -CHAT_HALF_EDGE,
      right: -CHAT_HALF_EDGE,
      width: CHAT_CORNER_ZONE_SIZE,
      height: CHAT_CORNER_ZONE_SIZE,
    },
  },
];

export const ClassicLayout = ({
  state,
  layoutToggle,
  chrome,
  canvasScrollRef,
  canvasSlotRef,
  clearConfirmOpen,
  setClearConfirmOpen,
  initialRecoverSeq,
  onRecoverDeepLinkConsumed,
  whatsNewAutoOpen,
  onWhatsNewAutoOpened,
}: ClassicLayoutProps): React.ReactNode => {
  const {
    transport,
    view,
    sendChat,
    setRom,
    sendKick,
    sendClear,
    leave,
    pushSystemLine,
    clearChatLog,
    // Phase 7 Task 7-10b: client-local uid filter — drawing engine と
    // ChatPanel の両方で参照する単一 source-of-truth.
    filteredUids,
    toggleFilter,
    // 2026-05-27 上部バー再編 (consult 確定): owner 判定に adminToken の有無を使う.
    // RoomSettingsModal と同じ AdminTokenStore (SessionProvider 所有) を流用する.
    adminTokens,
    // conflict-aware Undo (2026-07-23 是正): `stroke.cancel` 送信。Modern と同じ
    // `useSession().sendCancelStroke` (`stroke.cancel` command wrapper)。
    sendCancelStroke,
    sendCancelRegion,
    // custom-cursor-2026-09-19.md §2.3: カーソルエディタ (CursorEditorPopup) が使う。
    sendCursorChange,
    sendCursorDraft,
  } = useSession();

  // §9.7 Phase 2: 描画状態 store は RoomLayout が所有し props.chrome で受け取る
  // (= engine/canvas/overlay は CanvasStage が永続描画)。ClassicLayout は chrome の
  // ビューとして pen/color/mask/pressure/zoom/layer を読み書きする。下の JSX は従来
  // どおり `pen` / `engine` / `zoom` 等のローカル名を参照するので、chrome から alias を
  // 取り出して名前を温存する (= JSX を最小改変、Classic 見た目・挙動を不変に保つ)。
  const {
    pen,
    paletteSlots,
    selectedPaletteIdx,
    activePenWidths,
    activeSelectedPenWidthIndex,
    onSelectPen,
    // 2026-06-19 テクスチャブラシ Classic 露出 (modern-layout.md §1.1 表現パリティ).
    // Modern と同じ共有 chrome 経路 (= pen.texturePreset を書く / penParamsFor が
    // pen='texture' のときだけ wire/engine に流す)。Classic は入口ボタン → floating
    // ピッカーから呼ぶ。
    texturePreset,
    onSelectTexturePreset,
    // エアブラシ grain/density (airbrush-design-2026-08-15.md §6、表現パリティ原則で
    // Classic にも露出)。texture preset と同じ経路 (共有 PenKindPicker が相乗り)。
    airbrushGrain,
    onAirbrushGrainChange,
    airbrushDensity,
    onAirbrushDensityChange,
    // クレヨン/パステルの目の粗さ (phase11-light-pens-2026-08-21.md §2.6 / §6、表現パリティ
    // 原則で Classic にも露出)。airbrush grain と同じ経路 (共有 PenKindPicker が相乗り)。
    crayonGrain,
    onCrayonGrainChange,
    pastelGrain,
    onPastelGrainChange,
    // トーン (封印解除) の番手/間隔・ドットの間隔 (同 §4.5-§4.7 / §3 / §6、表現パリティ
    // 原則で Classic にも露出)。同じ経路 (共有 PenKindPicker が相乗り)。
    toneDensity,
    onToneDensityChange,
    tonePitch,
    onTonePitchChange,
    dotSpacing,
    onDotSpacingChange,
    onToggleEraser: handleToggleEraser,
    onSelectBrush,
    onSelectEraserDirect,
    // CSP流 循環ショートカット (2026-08-21): tool.pen.* / tool.tab.* の受け皿。
    onSelectPenRef,
    onSelectTabRef,
    shortcutCyclePositions,
    onShortcutCycleIndexChange,
    onWidthChange,
    onPenWidthCellClick,
    onPenWidthValueChange,
    onColorChange,
    onAlphaChange,
    onPaletteSlotSelect,
    onPaletteReset,
    slot1,
    slot2,
    maskTypeUI,
    onMaskSlotClick,
    onMaskTypeToggle,
    currentPressure,
    onPressureChange,
    penOnlyMode,
    setPenOnlyMode,
    setPanActive,
    isStrokeActive,
    layerVisibilityMap,
    handleSelectActiveLayer,
    handleToggleLayerVisible,
    layerOpacities,
    layerOpacityControl,
    layerThumbnailHub,
    layerThumbnailsEnabled,
    onLayerThumbnailsEnabledChange,
    layerThumbnailsStopped,
    backgroundVisible,
    handleToggleBackgroundVisible,
    backgroundRgb,
    handleBackgroundColorChange,
    canSetBackground,
    nameLabelMode,
    zoom,
    displayScale,
    // 2026-06-07 キャンバス左右反転 (view flip): engine が所有する flipX 状態と
    // トグルを共有 chrome 経由で読む (= K/O 共有化と同流儀)。flipX は ToolPanel の
    // 反転トグル UI、V キー listener が参照。DrawingCanvas/overlay 側の flip 配線は
    // CanvasStage が chrome.flipX を読んで行う。
    flipX,
    handleToggleFlip,
    handleToggleNameLabelMode,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
    // 決定3 §6 (consult 2026-09-29): 全体表示 (フィット) ショートカット `f` の配線
    // 先。Classic は chrome 凍結方針でボタンは足さず、ショートカットのみ (M1-a の
    // 入室時フィットと同じ chrome.applyFitZoom)。
    applyFitZoom,
    canImagepaste,
    canClear,
    handleClear,
    handleCapture,
    imagepaste,
    // canvasRef はプレビュー (PreviewOverlay) / imagepaste overlay の bounding rect
    // 読みで使う (= 実 canvas は CanvasStage が描画、ref は chrome 経由で共有)。
    canvasRef,
  } = chrome;

  // conflict-aware Undo (2026-07-23 是正、modern-layout.md §1.1 表現パリティ):
  // `chrome.engine` (= CanvasDrawingEngine、§9.7 共有・永続) を渡し、cancel 可能
  // スタック + 拒否通知の購読と `stroke.cancel` 送信を配線する。ModernLayout と同一の
  // フック・同一の名前解決ロジック (`state.members` から、自分自身は「自分」表示)。
  // 受け皿はショートカット (Ctrl/Cmd+Z、shortcutRegistry.ts `edit.undo`) + 拒否
  // トースト/bbox ハイライト (下記 JSX) に加え、2026-07-23 追補で上部バーの常設
  // ボタンも追加した (下記 topBarItems の 'undo' 項目、= Classic の確立流儀
  // 「ToolPanel に入らないボタンは上部バーへ」に従って作者本人が指摘、タッチのみの
  // タブレット環境でも Undo に到達できるようにする)。ToolPanel (chrome 凍結方針) 自体
  // には手を入れない。
  const resolveConflictDisplayName = useCallback(
    (uid: string): string | null => {
      if (uid === state.uid) return '自分';
      return state.members.find((m) => m.uid === uid)?.displayName ?? null;
    },
    [state.uid, state.members],
  );
  const connectionLocked = useConnectionLocked();
  const undo = useConflictAwareUndo({
    engine: chrome.engine,
    sendCancelStroke,
    sendCancelRegion,
    floatingRegion: {
      isFloating: chrome.region.phase.kind === 'floating',
      cancelFloating: chrome.region.cancel,
    },
    resolveDisplayName: resolveConflictDisplayName,
  });

  const [settingsOpen, setSettingsOpen] = useState(false);
  // 2026-05-27 上部バー再編 (consult 確定): owner 判定 (= adminToken 有無).
  // AdminTokenStore.get(roomId) は localStorage 直読みで非リアクティブなので、
  // 「ルーム設定」ボタンの見た目 (控えめ⇄琥珀アクセント) を駆動するため state 化する.
  // - 初期値: mount 時の token 有無.
  // - リアクティブ性: ユーザが RoomSettingsModal でログイン (token 取得) → 閉じた
  //   後にボタンが認証済表示へ更新される必要がある. AdminTokenStore の変化を
  //   subscribe する仕組みは無いので、モーダルを **閉じる経路** (closeSettings) で
  //   再評価して stale を防ぐ. これでログイン直後にボタンが古いまま固まらない.
  const [isOwner, setIsOwner] = useState<boolean>(() => adminTokens.get(state.roomId) !== null);
  // モーダル close 時に owner 判定を再評価する単一経路. RoomSettingsModal の
  // onClose にこれを渡す (= 直接 setSettingsOpen(false) を渡さない).
  const closeSettings = useCallback((): void => {
    setSettingsOpen(false);
    setIsOwner(adminTokens.get(state.roomId) !== null);
  }, [adminTokens, state.roomId]);
  // §9.7 Phase 2: 全消し確認モーダルの open/close は RoomLayout が所有し props で
  // 受け取る (= chrome.handleClear が setClearConfirmOpen(true) を叩く)。Classic の
  // ConfirmDialog はこの prop を読む。

  // 2026-05-17 個人 UI スケール設定 (consult 2026-05-17 確定): 左サイドバーを
  // 6 段階で拡縮する. 永続化は localStorage のみ (`oekaki:ui-scale-sidebar`),
  // サーバ同期なし, protocol 変更なし. 既存ユーザは何もしなくても 'medium'
  // (= 110%) で見える.
  //
  // 2026-05-31 chat-scale-cleanup: 旧「チャット欄の大きさ」(= bottom row wrapper
  // 全体 zoom) は既存 splitter (chatRowPx / memberColPx の drag) と機能重複だった
  // ため撤去. 代わりに「チャット文字サイズ」を `chatFontScalePreference` 経由で
  // 別軸として持つ (= 行高 / 列幅は変えず文字だけ em 倍率で拡縮).
  //
  // store は module-scope ではなく useRef で 1 インスタンスだけ作る (= ClassicLayout
  // の lifecycle に紐づける必要は本来無いが, テスト時に storage mock を差し替え
  // やすい単一管轄に閉じる). lazy initializer で localStorage を読み出し、値が
  // 変わるたびに setter を呼んで永続化する.
  const uiScaleStoreRef = useRef(createUiScalePreferenceStore());
  const chatFontScaleStoreRef = useRef(createChatFontScalePreferenceStore());
  // プレビュー窓のサムネ最大辺 (px) の永続化 store (= preview-resize.md §3, ID=H).
  // uiScaleStore / chatFontScaleStore と同じく ClassicLayout が 1 個所有し、
  // PreviewOverlay には初期値 (store.get()) と確定コールバック (store.set) を渡す.
  const previewSizeStoreRef = useRef(createPreviewSizePreferenceStore());
  const [uiPrefsOpen, setUiPrefsOpen] = useState<boolean>(false);
  // 2026-06-12 ショートカット設定モーダル (shortcut-settings.md スライス2): ヘッダ
  // 「ショートカット」項目 + `?` ホットキーから開く (= 「表示」→UserPreferencesModal の mirror)。
  const [shortcutsOpen, setShortcutsOpen] = useState<boolean>(false);
  // 2026-06-13 続報 (consult): ペン設定モーダル (手ぶれ補正 / AA)。ヘッダ「ペン」項目
  // から開く (= 「ショートカット」+ ShortcutSettingsModal の前例を踏襲した独立モーダル)。
  const [penSettingsOpen, setPenSettingsOpen] = useState<boolean>(false);
  // T3 絵ログ (タイムラプス) X 共有 (growth-timelapse-share-2026-07-02.md §5.3.8):
  // Classic 上部バーにも入口を露出する (Modern の ModernCommandCluster 入口と同じ
  // support gate / モーダル配線)。isTimelapseExportSupported (WebCodecs H.264 実 probe)
  // が false の環境では上部バー項目自体を出さない (disabled でなく非表示、§5.2)。
  const timelapseSupported = useTimelapseExportSupported();
  const [timelapseOpen, setTimelapseOpen] = useState<boolean>(false);
  // 「New!」バッジ (consult 2026-07-08 案B): 初回にボタンを押したら消す。seen フラグは
  // localStorage 永続 (Classic / Modern 共有 key)。押下経路 (handleTimelapseEntryClick) で
  // markSeen + state 反映するので、押した直後にバッジが消える。
  const timelapseSeenStoreRef = useRef(createTimelapseShareSeenStore());
  const [timelapseSeen, setTimelapseSeen] = useState<boolean>(() =>
    timelapseSeenStoreRef.current.hasSeen(),
  );
  // 2026-07-08 方針変更: 絵ログ入口は **全員に見せる** (flag+support のみで gate)。
  // 押下時にオーナー判定で分岐する:
  //   - オーナー (isOwner=true, adminToken 保有) → 従来どおり絵ログモーダルを開く。
  //   - 非オーナー → 「ルーム設定」ボタンと同じ流儀で RoomSettingsModal を開く。
  //     RoomSettingsModal は adminToken 無しなら UnauthenticatedNotice を出し、そこの
  //     「ログイン画面へ」CTA でオーナーログイン (/login) へ誘導する (= room-settings と
  //     同一遷移)。履歴 DL は既に owner 認証必須なので、非オーナーは誘導止まりで fetch に
  //     到達せず、同意なし公開の抑止は保たれる。
  // どちらの押下でも New! バッジは seen 化する (= 一度でも押したら「見た」扱い)。
  const handleTimelapseEntryClick = useCallback((): void => {
    timelapseSeenStoreRef.current.markSeen();
    setTimelapseSeen(true);
    if (isOwner) {
      setTimelapseOpen(true);
    } else {
      setSettingsOpen(true);
    }
  }, [isOwner]);
  // 過去キャンバス一覧 (Slice 2、past-canvas-new-document-2026-07-05.md §4.2/§7):
  // 絵ログ入口の隣に独立入口を出す。可視性は絵ログと同じ「全員表示 + 非オーナーは
  // ログイン誘導」(feb5937 パターン)。押下でオーナー分岐 (オーナー→一覧モーダル /
  // 非オーナー→設定モーダル誘導)。New! バッジは別 key の seen store で管理し、
  // どちらの押下でも markSeen する。
  const [pastCanvasOpen, setPastCanvasOpen] = useState<boolean>(false);
  // 荒らし復旧 UI「?recover=<seq>」深リンク (vandalism-recovery-ui-2026-09-27.md
  // §2「入口を『続きを描く』に統一」): 一覧モーダルを開いた時点の seq を保持し、
  // `PastCanvasListModal` の `initialRecoverSeq` に橋渡しする (= モーダルが開いた
  // 「後」に親の `initialRecoverSeq` prop がクリアされても、開いた瞬間の値をこの
  // state に確定させているので、当該インスタンスの寿命中は保持され続ける)。
  const [pastCanvasRecoverSeq, setPastCanvasRecoverSeq] = useState<number | undefined>(undefined);
  const pastCanvasSeenStoreRef = useRef(createPastCanvasSeenStore());
  const [pastCanvasSeen, setPastCanvasSeen] = useState<boolean>(() =>
    pastCanvasSeenStoreRef.current.hasSeen(),
  );
  const handlePastCanvasEntryClick = useCallback((): void => {
    pastCanvasSeenStoreRef.current.markSeen();
    setPastCanvasSeen(true);
    if (isOwner) {
      setPastCanvasOpen(true);
    } else {
      setSettingsOpen(true);
    }
  }, [isOwner]);
  // mount 時に深リンクの seq があれば一覧モーダルを自動で開く。RoomLayout 側の
  // `initialRecoverSeq` は 1 回読んだらこのレイアウトの寿命中変わらない前提
  // (App.tsx の effect が mount 時に 1 回だけ設定 → ここで消費 → クリア) なので、
  // 依存配列を絞って mount 時 1 回だけ走らせる。
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount 時に一度だけ消費する (initialRecoverSeq は App.tsx 側で 1 回しか設定されない)。
  useEffect(() => {
    if (initialRecoverSeq === undefined) return;
    setPastCanvasRecoverSeq(initialRecoverSeq);
    setPastCanvasOpen(true);
    onRecoverDeepLinkConsumed?.();
  }, []);
  // 使い方ボタン (作者フィードバック 2026-09-22「もっと目立つようにできないかな」):
  // 初回クリックまで「New!」バッジを出す。押下は owner 分岐せず (全員がマニュアルを
  // 見られる)、seen 化 + 別タブ遷移のみ。
  const helpSeenStoreRef = useRef(createHelpButtonSeenStore());
  const [helpSeen, setHelpSeen] = useState<boolean>(() => helpSeenStoreRef.current.hasSeen());
  const handleHelpEntryClick = useCallback((): void => {
    helpSeenStoreRef.current.markSeen();
    setHelpSeen(true);
    window.open(buildHelpUrl('classic'), '_blank', 'noopener,noreferrer');
  }, []);
  const [sidebarScale, setSidebarScaleState] = useState<UiScale>(() =>
    uiScaleStoreRef.current.getSidebar(),
  );
  const [chatFontScale, setChatFontScaleState] = useState<ChatFontScale>(() =>
    chatFontScaleStoreRef.current.get(),
  );
  // Setter 経由で「state 更新 + localStorage 書き込み」を 1 つの動作に集約する.
  // Save ボタン無しの即時反映なので, change 経路を 1 本化して取り違えを防ぐ.
  const setSidebarScale = useCallback((next: UiScale): void => {
    setSidebarScaleState(next);
    uiScaleStoreRef.current.setSidebar(next);
  }, []);
  const setChatFontScale = useCallback((next: ChatFontScale): void => {
    setChatFontScaleState(next);
    chatFontScaleStoreRef.current.set(next);
  }, []);
  // 2026-08-12 入室時サイズオーバー救済 (作者判断、B-1 の続き):
  // iPad-like 端末で「実測して縦に入りきらない」ときだけ、**localStorage の保存値
  // を無視して**最小段 (xxsmall) に倒す。
  //
  // 【なぜ default 変更だけでは足りないか】`IPAD_DEFAULT_UI_SCALE` の xxsmall 化は
  // localStorage に値が無いユーザーにしか効かない。ところが α/β 期に iPad で触った
  // ユーザーは既に「入りきらないスケール」を保存済みの可能性が高く (= フィード
  // バックをくれた層がまさにそれ)、default 変更では 1 人も救われない。
  //
  // 【安全弁】判定と適用の方針は `rescueSidebarScale` (pure) 側に集約してある:
  // localStorage は書き換えない / 下げる方向にしか動かさない / iPad-like 限定。
  // ここが持つのは「実測」だけ。
  //
  // 実測は B-1 で入れた `overflow-y: auto` の副産物である `scrollHeight >
  // clientHeight` を使う (= スクロールできる = 入りきっていない、と同義)。
  // 1px 未満の端数で誤発火しないよう 1px の許容を置く。
  //
  // タイミング: mount 直後は fieldset 内の実寸 (フォント・ボタン) が確定しきって
  // いないことがあるので、layout effect 即時 + 次フレームの 2 回測る (= 同 doc 内の
  // scroll リセットや splitterLeftPx と同じ流儀)。**一度倒したら戻さない**
  // (= 回転や resize で測り直して行ったり来たりさせない。ユーザーが設定 UI で
  // 選び直せば `setSidebarScale` が通常どおり勝つ)。
  const sidebarRescuedRef = useRef(false);
  useLayoutEffect(() => {
    if (sidebarRescuedRef.current) return undefined;
    const isIpadLike = defaultDetectIpadLike();
    if (!isIpadLike) return undefined;
    const measure = (): void => {
      if (sidebarRescuedRef.current) return;
      const el = leftColRef.current;
      if (el === null) return;
      // uiHidden (= Tab で UI を畳んだ状態) では実寸が 0 になるので測らない。
      if (el.clientHeight <= 0) return;
      const overflows = el.scrollHeight > el.clientHeight + 1;
      const next = rescueSidebarScale(sidebarScale, overflows, isIpadLike);
      if (next === sidebarScale) return;
      sidebarRescuedRef.current = true;
      // localStorage には書かない (= 保存値を保持する) ので setSidebarScale では
      // なく state setter を直接呼ぶ。
      setSidebarScaleState(next);
    };
    measure();
    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [sidebarScale]);
  // wrapper の inline `zoom` に流す数値. uiScaleToCssZoom が pure なのでメモ不要だが,
  // 同 frame で複数回参照するので useMemo で 1 計算に纏める.
  const sidebarZoom = useMemo(() => uiScaleToCssZoom(sidebarScale), [sidebarScale]);
  // 2026-05-31 chat-scale-cleanup: ChatPanel の log + input + popup input に
  // 当てる em 倍率 (= chatFontScaleToEm 結果). pure なのでメモは原則不要だが
  // 同 frame で複数箇所参照することを想定して useMemo に揃える.
  const chatFontEm = useMemo(() => chatFontScaleToEm(chatFontScale), [chatFontScale]);

  // 2026-05-17 追補 (個人 UI スケールの続き): grid 境界 drag-resize.
  // chat 行高 (= gridTemplateRows 下行) / member 列幅 (= gridTemplateColumns 3 列目)
  // の raw px を localStorage に永続化する.
  // 2026-05-31 chat-scale-cleanup: 旧「チャット欄の大きさ」(= bottom row wrapper
  // 全体 zoom) を撤去したので、grid template には raw chatRowPx / memberColPx を
  // そのまま流す (= zoom 倍掛けが消えて素直な実装に戻る).
  const paneSizeStoreRef = useRef(createPaneSizePreferenceStore());
  const [chatRowPx, setChatRowPxState] = useState<number>(() =>
    paneSizeStoreRef.current.getChatRowPx(),
  );
  const [memberColPx, setMemberColPxState] = useState<number>(() =>
    paneSizeStoreRef.current.getMemberColPx(),
  );
  // Save ボタン無しの即時永続化なので、change 経路を 1 本化 (= setState +
  // localStorage を同じ setter にまとめる). clamp は store 側でも入るが、
  // UI 側 (slider / drag) でも事前 clamp して表示と保存値の差異を避ける.
  const setChatRowPx = useCallback((next: number): void => {
    const clamped = clampChatRowPx(next);
    setChatRowPxState(clamped);
    paneSizeStoreRef.current.setChatRowPx(clamped);
  }, []);
  const setMemberColPx = useCallback((next: number): void => {
    const clamped = clampMemberColPx(next);
    setMemberColPxState(clamped);
    paneSizeStoreRef.current.setMemberColPx(clamped);
  }, []);
  // grid 親の DOMRect 参照. splitter drag 中に pointer 座標を grid 親座標へ
  // 変換するために使う (= 親の bottom / right から逆算して raw px を出す).
  const gridParentRef = useRef<HTMLDivElement | null>(null);
  // 2026-06-13 sidebar 越境 fix (classic-sidebar-compaction.md §8): 左カラム
  // wrapper の参照. 列幅が `minmax(60 * sidebarZoom px, max-content)` で中身
  // 追従に変わったため、横 splitter の left を定数式 (16 + 60 * sidebarZoom)
  // ではなく実測 (= getBoundingClientRect) で出す必要がある (下の
  // splitterLeftPx effect 参照).
  const leftColRef = useRef<HTMLDivElement | null>(null);
  // chat-member-row (= chat 列 + member 列を含む横並び flex container) の
  // DOMRect 参照. 縦 splitter (= member 列幅変更) drag の anchor として使う.
  // docked / detached どちらでも同じ親要素なので、chatDetached 切替後も
  // splitter を機能させられる (= 旧 grid 親 anchor では detached 時に外れていた).
  const chatMemberRowRef = useRef<HTMLDivElement | null>(null);
  // Phase 6.7 サブ task (consult 2026-05-07 → 修正 → controlled 化): カラー
  // サークル floating palette の表示状態. パレット slot を dblclick したとき
  // 開く. controlled 化に伴い「初期色」の保持は不要になり、open/closed の
  // boolean フラグだけで十分 (現在色は `value={{rgb24: pen.color, alpha255:
  // pen.alpha}}` で直接流す → palette slot を別 slot に切替えると wheel/slider
  // も追従).
  // 閉じる経路は × ボタン + ESC のみ (= 背景クリックは廃止、描画と並行操作可).
  const [colorWheelOpen, setColorWheelOpen] = useState<boolean>(false);
  // A-4 背景色 ■ swatch (canvas-background-color §4.3): 背景色 ColorWheelPalette の
  // open-state。draw 色 colorWheelOpen とは別 (= 同時に別の色を弄れる)。LayerPopup の
  // ■ click が onToggleBackgroundColorWheel でこれを反転する。
  const [bgColorWheelOpen, setBgColorWheelOpen] = useState<boolean>(false);
  // 2026-05-17 旧版 PreviewFrame 復活 (旧 PreviewCanvas.java / PreviewFrame.java
  // 踏襲): キャンバス全体プレビュー floating panel の開閉. ON で画面右下に
  // 縮小キャンバス + 表示範囲枠 + クリック/ドラッグでスクロール位置変更 UI を
  // 出す. ColorWheelPalette と同じ floating panel 流儀 (= backdrop 無し、描画と
  // 並行操作可、× ボタン + ESC で閉じる).
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);

  // custom-cursor-2026-09-19.md §2.5 エディタ: 「カーソル」フロート開閉.
  // PenPopup と違い ephemeral (= localStorage 永続化しない、押した時だけ使う
  // 一時的な作業窓、`cursorEditorPopup.ts` 冒頭コメント参照).
  const [cursorEditorOpen, setCursorEditorOpen] = useState<boolean>(false);
  // custom-cursor-ux フォローアップ (2026-09-19): 「一度も開いたことがない端末」判定。
  // MemberPanel の自分の行「＋」プレースホルダを pulse させるかどうかに使う (= 発見性の
  // ワンショット演出、開いたら以後出さない). 初期値は mount 時の localStorage 値.
  const [cursorHintSeen, setCursorHintSeen] = useState<boolean>(() =>
    typeof window === 'undefined' ? true : readCursorEditorHintSeen(window.localStorage),
  );
  // カーソル入口ボタン (上部バー「カーソル」) の「New!」バッジ seen フラグ (2026-09-19
  // 追補)。上記 cursorHintSeen (MemberPanel プレースホルダの pulse) とは別の演出・別
  // localStorage key だが、「開いたら消える」トリガは同じなので同じ funnel
  // (`markCursorHintSeenIfNeeded` / `openCursorEditor`) に相乗りさせる。Modern
  // `ModernToolRail` と同じ store key を共有 (= どちらのレイアウトのどの入口で開いても
  // 両方のバッジが消える).
  const cursorNewBadgeSeenStoreRef = useRef(createCustomCursorNewBadgeSeenStore());
  const [cursorNewBadgeSeen, setCursorNewBadgeSeen] = useState<boolean>(() =>
    cursorNewBadgeSeenStoreRef.current.hasSeen(),
  );
  // 「開いた」を記録する単一の口 (どの入口からでも通す): ToolPanel の [カーソル] トグルと
  // MemberPanel の自分の行の両方がこれ経由でエディタを開閉する.
  const markCursorHintSeenIfNeeded = useCallback((): void => {
    setCursorHintSeen((seen) => {
      if (!seen && typeof window !== 'undefined') markCursorEditorHintSeen(window.localStorage);
      return true;
    });
    cursorNewBadgeSeenStoreRef.current.markSeen();
    setCursorNewBadgeSeen(true);
  }, []);
  const toggleCursorEditor = useCallback((): void => {
    setCursorEditorOpen((prev) => {
      const next = !prev;
      if (next) markCursorHintSeenIfNeeded();
      return next;
    });
  }, [markCursorHintSeenIfNeeded]);
  // custom-cursor-ux フォローアップ: 参加者リストの自分の行 (サムネ / 「＋」) から常に
  // 「開く」(トグルではない、= 既に開いていても閉じない) 単一の口.
  const openCursorEditor = useCallback((): void => {
    setCursorEditorOpen(true);
    markCursorHintSeenIfNeeded();
  }, [markCursorHintSeenIfNeeded]);
  // 自分の確定済みカーソル (= ToolPanel の [カーソル] ボタンのサムネ、CursorEditorPopup の
  // 編集初期値の両方で使う単一の導出値).
  const selfCursor = useMemo(
    () => state.members.find((m) => m.uid === state.uid)?.cursor ?? null,
    [state.members, state.uid],
  );

  // 2026-06-20 ペン種選択をフロート「ペン」に集約 (consult 2026-06-20、A 採択)。
  // ToolPanel の [ペン] 入口ボタン ([プレビュ]/[レイヤー] 同型) が開閉する常設フロート
  // 「ペン」(PenPopup) の可視 state。LayerPopup の visibility toggle と同型に ClassicLayout
  // が単一 source-of-truth で持ち、localStorage に永続化する。default は発見性のため open
  // (= ペン種ボタンが ToolPanel から消えるため、初見ユーザがペン種に気づけるよう、
  // consult 2026-06-20「初回は発見性のため open 既定を推奨」)。panel 位置は PenPopup 内で
  // 別途記憶 (`penPopup.ts`)。
  const penPopupVisibilityStoreRef = useRef(createPenPopupVisibilityStore());
  const [penPopupOpen, setPenPopupOpenState] = useState<boolean>(() =>
    penPopupVisibilityStoreRef.current.getInitial(),
  );
  // Setter 経由で「state 更新 + localStorage 書き込み」を 1 つの動作に集約する
  // (= LayerPopup の toggleLayerPopupVisible と同流儀)。
  const togglePenPopup = useCallback((): void => {
    setPenPopupOpenState((prev) => {
      const next = !prev;
      penPopupVisibilityStoreRef.current.set(next);
      return next;
    });
  }, []);
  // preset 選択で pen='texture' に入りつつ preset をセット (= Modern と同じ
  // onSelectTexturePreset / penParamsFor 経路、engine 変更なし)。texture をまだ
  // 選んでいない (= [ブラシ]/[フラット]/消ゴム 中) 状態でサムネを押したら texture
  // へ切替える。既に texture なら preset 差替のみ。
  const handleSelectTexturePreset = useCallback(
    (preset: TexturePreset): void => {
      onSelectTexturePreset(preset);
      if (pen.pen !== 'texture') onSelectPen('texture');
    },
    [onSelectTexturePreset, onSelectPen, pen.pen],
  );

  // §12 方針変更 (floating-panels-2026-08-01.md、2026-08-01 consult 後):
  // Classic サイドバー全体 (= 白背景ボックス [PalettePanel+ToolPanel+
  // PenPressureConfig] + ArgbSliderPanel + MaskPanel、旧 Java 版 `paintToolFrame`
  // 相当) の detach / 自由配置。§1「既定は現行配置のまま」を守るため既定は docked
  // (= `sidebarFloatOpen === false`、classicToolFloat.ts のコメント参照)。
  //
  // 導線は 2 つ:
  //   1. ドラッグ (= `leftColRef` wrapper 全体を掴める。NO_SHELL_DRAG_SELECTOR で
  //      ボタン/入力/canvas を除外するだけなので、既定表示に新しい chrome は
  //      **一切増えない** = §1 を保ったままドラッグ導線を足せる)。
  //   2. `/open tool` コマンド (旧 Java 版パリティの明示的な回復)。
  // 復帰は 3 つ: ⇤ドック ボタン / `/close tool` / 「配置をリセット」。
  //
  // leftColRef は docked (grid セル) と floating (position:fixed) で**同じ DOM
  // ノードを使い回す** (= remount しない、chatDetached の既存パターンを踏襲)。
  // detach ドラッグ中も pointer capture が同じ要素に残り続けるため、閾値を
  // 超えた瞬間に docked→floating へ切り替えてもドラッグが途切れない。
  const sidebarFloatOpenStoreRef = useRef(createToolFloatOpenStore());
  const [sidebarFloatOpen, setSidebarFloatOpenState] = useState<boolean>(() =>
    sidebarFloatOpenStoreRef.current.getInitial(),
  );
  const setSidebarFloatOpen = useCallback((next: boolean): void => {
    setSidebarFloatOpenState(next);
    sidebarFloatOpenStoreRef.current.set(next);
  }, []);
  // `/open tool`: idempotent (= 既にフロート中なら何もしない、二重 open で位置が
  // リセットされるのを防ぐ)。
  const handleOpenToolFloat = useCallback((): void => {
    setSidebarFloatOpen(true);
  }, [setSidebarFloatOpen]);
  // 2026-08-01 追加依頼: サイドバーの右ドック対応。既定は 'left' (§1 既定不変)。
  // 判定ロジックは増やさず、既存の edge-snap (resolveEdgeSnapSide) の結果や
  // 位置ベース ⇤ドック (floatPanelSide、§11 パターン) をそのままこの state に
  // 書き込むだけ (= 「どちら側に着地したか」を覚えておく永続 state)。
  const sidebarDockSideStoreRef = useRef(createSidebarDockSideStore());
  const [sidebarDockSide, setSidebarDockSideState] = useState<SidebarDockSide>(() =>
    sidebarDockSideStoreRef.current.getInitial(),
  );
  const setSidebarDockSide = useCallback((next: SidebarDockSide): void => {
    setSidebarDockSideState(next);
    sidebarDockSideStoreRef.current.set(next);
  }, []);
  const [sidebarFloatPos, setSidebarFloatPosState] = useState<ClassicToolFloatPosition>(() => {
    if (typeof window === 'undefined') return defaultToolFloatPosition(0, 0);
    const stored = readToolFloatPosition(window.localStorage);
    if (stored !== null) return stored;
    return defaultToolFloatPosition(window.innerWidth, window.innerHeight);
  });
  // 実測 helper。leftColRef は docked/floating どちらでも同じノードを指すので
  // 常に実寸が取れる (= nominal 定数は mount 直前の 1 フレームだけの fallback)。
  const measureSidebar = useCallback((): { width: number; height: number } => {
    const el = leftColRef.current;
    if (el !== null) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return { width: r.width, height: r.height };
    }
    return { width: TOOL_FLOAT_NOMINAL_WIDTH, height: TOOL_FLOAT_NOMINAL_HEIGHT };
  }, []);
  const persistSidebarFloatPos = useCallback((next: ClassicToolFloatPosition): void => {
    if (typeof window === 'undefined') return;
    writeToolFloatPosition(window.localStorage, next);
  }, []);
  // 位置ベース ⇤ドック対象 (§11 パターン、Modern の `floatPanelSide` をそのまま
  // 再利用): 現在のフロート位置が画面の左半分/右半分どちらに近いか。ドッキング
  // 判定ロジックを増やさないため既存 `floatPanelSide` を呼ぶだけ。ボタンの
  // ラベル表示と実際の redock 先を同じ値で揃えるため render 時に 1 回だけ計算し、
  // 両方がこの 1 つの値を参照する (= 表示とふるまいの不一致を構造的に防ぐ)。
  const sidebarRedockSide: SidebarDockSide = floatPanelSide(
    sidebarFloatPos,
    { width: TOOL_FLOAT_NOMINAL_WIDTH, height: TOOL_FLOAT_NOMINAL_HEIGHT },
    {
      width: typeof window === 'undefined' ? 0 : window.innerWidth,
      height: typeof window === 'undefined' ? 0 : window.innerHeight,
    },
  );
  // ⇤ドック ボタン / `/close tool`: 現在のフロート位置に一番近い側へドックする
  // (2026-08-01 追加依頼「位置ベース ⇤ドック」、上の sidebarRedockSide と同じ値を
  // 使うのでボタン表示と実際の着地先が常に一致する)。
  const handleCloseToolFloat = useCallback((): void => {
    setSidebarDockSide(sidebarRedockSide);
    setSidebarFloatOpen(false);
  }, [sidebarRedockSide, setSidebarDockSide, setSidebarFloatOpen]);
  // ドラッグ中の一時 ref/state。
  const sidebarGrabOffsetRef = useRef<FloatPoint | null>(null);
  // ドラッグ開始時点で docked/floating どちらだったか (= キャンセル時の復帰先)。
  const sidebarWasFloatingBeforeDragRef = useRef<boolean>(false);
  // ドラッグ開始時点の position (= キャンセル時に位置も巻き戻す。floating 中の
  // reposition をキャンセルしても「途中まで動いた場所」に残ってしまうバグを防ぐ)。
  const sidebarPosBeforeDragRef = useRef<ClassicToolFloatPosition>(sidebarFloatPos);
  // §11 エッジスナップ再利用 (floating-panels-2026-08-01.md §11、判定系を二重に
  // 作らない): ドラッグ中、画面のどちら側の端に近いか (= 'left'/'right'/null。
  // 2026-08-01 追加依頼で右ドックにも対応したため両端を見る。判定関数自体は
  // 既存の `resolveEdgeSnapSide` そのまま、新しい判定ロジックは足さない)。
  const [sidebarNearDockEdge, setSidebarNearDockEdge] = useState<'left' | 'right' | null>(null);
  const sidebarGrip = usePanelDragGrip({
    onDragStart: (offset) => {
      sidebarGrabOffsetRef.current = offset;
      sidebarWasFloatingBeforeDragRef.current = sidebarFloatOpen;
      sidebarPosBeforeDragRef.current = sidebarFloatPos;
      if (!sidebarFloatOpen) {
        // detach 開始: 現在の docked 矩形をそのままフロート開始位置にする
        // (= 見た目をジャンプさせない)。以後は onDragMove がポインタに追従させる。
        const rect = leftColRef.current?.getBoundingClientRect();
        if (rect) setSidebarFloatPosState({ x: rect.left, y: rect.top });
        setSidebarFloatOpenState(true);
      }
    },
    onDragMove: (clientX, clientY) => {
      const offset = sidebarGrabOffsetRef.current ?? { x: 0, y: 0 };
      setSidebarFloatPosState(
        clampFloatPosition(
          floatPositionFromPointer(clientX, clientY, offset),
          measureSidebar(),
          { width: window.innerWidth, height: window.innerHeight },
          FLOAT_EDGE_INSET_PX,
        ),
      );
      const row = gridParentRef.current?.getBoundingClientRect();
      const side = row
        ? resolveEdgeSnapSide(
            { x: clientX, y: clientY },
            { left: row.left, top: row.top, width: row.width, height: row.height },
            EDGE_SNAP_THRESHOLD_PX,
          )
        : null;
      // 2026-08-01 追加依頼: 右ドック対応。`resolveEdgeSnapSide` は既に
      // 'left'/'right'/null を返す既存関数なので、そのままどちらの端かを
      // 保持するだけ (判定ロジックの追加なし)。
      setSidebarNearDockEdge(side);
    },
    onDragEnd: (committed) => {
      sidebarGrabOffsetRef.current = null;
      const nearEdge = sidebarNearDockEdge;
      setSidebarNearDockEdge(null);
      if (!committed) {
        // キャンセル (Escape / pointercancel): ドラッグ開始前の状態・位置へ復帰。
        setSidebarFloatOpenState(sidebarWasFloatingBeforeDragRef.current);
        setSidebarFloatPosState(sidebarPosBeforeDragRef.current);
        return;
      }
      if (nearEdge !== null) {
        // 画面端 (左 or 右) で確定 = その側のドックへ戻す (位置ベース ⇤ドックと
        // 同じ心的モデル、2026-08-01 追加依頼で右端にも対応)。
        setSidebarDockSide(nearEdge);
        setSidebarFloatOpen(false);
        return;
      }
      setSidebarFloatOpen(true);
      setSidebarFloatPosState((cur) => {
        persistSidebarFloatPos(cur);
        return cur;
      });
    },
    measureGrabOffset: (e) => {
      const rect = leftColRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
  });
  // シェル全体を掴み所にする pointerdown 入口 (= FloatShell と同じ判定、
  // NO_SHELL_DRAG_SELECTOR に一致する要素上では grip へ委譲しない。新しい
  // 可視 chrome を足さずにドラッグ導線を持たせる仕掛けの核)。
  const handleSidebarPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>): void => {
      if ((e.target as Element | null)?.closest(NO_SHELL_DRAG_SELECTOR)) return;
      sidebarGrip.onPointerDown(e);
    },
    [sidebarGrip.onPointerDown],
  );
  // window resize で画面外に取り残されたら引き戻す (フロート中のみ)。
  useEffect(() => {
    if (typeof window === 'undefined' || !sidebarFloatOpen) return;
    const onResize = (): void => {
      setSidebarFloatPosState((cur) => {
        const clamped = clampFloatPosition(
          cur,
          measureSidebar(),
          { width: window.innerWidth, height: window.innerHeight },
          FLOAT_EDGE_INSET_PX,
        );
        if (clamped.x !== cur.x || clamped.y !== cur.y) persistSidebarFloatPos(clamped);
        return clamped;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [sidebarFloatOpen, measureSidebar, persistSidebarFloatPos]);
  // 「配置をリセット」(= UserPreferencesModal「表示の設定」から呼ぶ、Modern の
  // preset リセットに相当する救済導線、§7 経路3): サイドバーを docked へ戻す。
  // 併せて社交ドック (チャット/メンバー) の detach も docked へ戻す (= Classic の
  // 自由配置をまとめて既定に戻す単一操作、Modern の「preset 選択で全パネルドック
  // 復帰」と同じ心的モデル)。2026-08-01 追加依頼: ドック側 (左/右) も既定 'left'
  // に戻す (= §1 既定不変の完全復元)。
  const handleResetClassicLayout = useCallback((): void => {
    setSidebarFloatOpen(false);
    setSidebarDockSide('left');
    setChatDetached(false);
  }, [setSidebarFloatOpen, setSidebarDockSide]);

  // 2026-05-17 旧版 `■→` ボタン復活 (旧 `ChatPanel.java:294,1542-1553` `openChatButton`
  // / `openTextChatFrame` 踏襲): チャット + 参加者リストを切り離して floating
  // frame 化する toggle. 旧版は `setChatVisible(false)` + 別 JDialog window だったが、
  // 新版は popup blocker 回避 + fullscreen 維持のため画面内 `position: fixed` の
  // floating panel で代替 (= PreviewOverlay / ColorWheelPalette と同流儀).
  // 切り離し中はメイン bottom row 全体が消えて canvas が画面下端まで延伸する
  // (= 絵描き中の縦領域確保、旧版の `layoutWhiteboard()` 相当).
  //
  // 切替時に ChatPanel / MemberPanel の内部 state (draft, 発言小窓 popup, filter)
  // を消さないため、両 component は同じ JSX 位置に維持し wrapper div の className
  // だけ切替える設計 (= remount 回避). inner wrapper には key を付け、title bar
  // 有無による位置ずれでの remount を防ぐ.
  const [chatDetached, setChatDetached] = useState<boolean>(false);
  // §12 (floating-panels-2026-08-01.md): `/open chat` / `/close chat` は
  // **既存の `chatDetached` にそのまま繋ぐだけ** (= 新機構は作らない、旧 Java 版
  // `openChatFrame()`/`closeChatFrame()` パリティ)。
  const handleOpenChatFloat = useCallback((): void => setChatDetached(true), []);
  const handleCloseChatFloat = useCallback((): void => setChatDetached(false), []);
  // 旧版 dialog 400x200 + 新版は member list を内包するので少し大きめ. ユーザは
  // 右下 resize handle で自由に変更でき, この初期値は detach 直後の見た目に
  // しか影響しない.
  const DETACHED_CHAT_INITIAL_WIDTH = 520;
  const DETACHED_CHAT_INITIAL_HEIGHT = 260;
  // 切り離し時の floating frame サイズ. resize handle で更新.
  const [detachedChatSize, setDetachedChatSize] = useState<FloatingPopupSize>({
    width: DETACHED_CHAT_INITIAL_WIDTH,
    height: DETACHED_CHAT_INITIAL_HEIGHT,
  });
  // 切り離し時の floating frame 左上座標. PreviewOverlay と同じく lazy initializer
  // で「右下寄り」を初期値にする. window 未定義 (SSR) なら fallback で 24,24.
  const [detachedChatPos, setDetachedChatPos] = useState<FloatingPopupPosition>(() => {
    if (typeof window === 'undefined') return { x: 24, y: 24 };
    const MARGIN = 16;
    return {
      x: Math.max(MARGIN, window.innerWidth - DETACHED_CHAT_INITIAL_WIDTH - MARGIN),
      y: Math.max(MARGIN, window.innerHeight - DETACHED_CHAT_INITIAL_HEIGHT - MARGIN),
    };
  });
  // 枠ドラッグ (8 方向) リサイズ操作用 anchor. title bar の drag anchor とは別
  // ref で並立させる (= 同時に発火することは UI 上ありえないが、handler 経路が
  // 分離している方が読みやすい). 掴んだゾーン + origin pos/size + pointer 起点を
  // 記録し、pointermove で `nextFloatingResize` に流して width/height を独立に動かす.
  const detachedChatResizeAnchorRef = useRef<{
    zone: FloatingResizeZone;
    anchor: Floating2DResizeAnchor;
  } | null>(null);
  // detach 窓 root 要素 ref. リサイズ確定時 / window resize 時に実寸
  // (getBoundingClientRect) を読んで `clampPopupPosition` で画面内へ引き戻す.
  const detachedChatRootRef = useRef<HTMLDivElement | null>(null);

  // detach 窓の実寸を読む helper. ref 未取得時は state の論理サイズで fallback.
  // PreviewOverlay の `measurePanel` 相当 (= getBoundingClientRect は zoom 後の
  // 実効サイズを返すのでそのまま clamp に使える).
  const measureDetachedChat = useCallback((): FloatingPopupSize => {
    const el = detachedChatRootRef.current;
    if (el !== null) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return { width: r.width, height: r.height };
    }
    return detachedChatSize;
  }, [detachedChatSize]);

  // リサイズ / 移動 確定時に detach 窓を画面内へ引き戻す (PreviewOverlay の
  // commitResize / window resize clamp と同じ意匠). 大きくして画面外へはみ出した
  // 分や、window 縮小で取り残された分を panel ごと戻す.
  const clampDetachedChatIntoView = useCallback((): void => {
    if (typeof window === 'undefined') return;
    setDetachedChatPos((cur) =>
      clampFloatPosition(
        cur,
        measureDetachedChat(),
        { width: window.innerWidth, height: window.innerHeight },
        CHAT_DETACH_EDGE_INSET_PX,
      ),
    );
  }, [measureDetachedChat]);

  // S3 スライス2: title bar drag は共有 usePanelDragGrip (= 他 4 popup / 新規
  // toolFloat と同じ配管、preventDefault 漏れの潜在バグを構造的に解消)。
  const chatDetachGrabOffsetRef = useRef<FloatPoint | null>(null);
  const chatDetachGrip = usePanelDragGrip({
    onDragStart: (offset) => {
      chatDetachGrabOffsetRef.current = offset;
    },
    onDragMove: (clientX, clientY) => {
      const offset = chatDetachGrabOffsetRef.current ?? { x: 0, y: 0 };
      setDetachedChatPos(
        clampFloatPosition(
          floatPositionFromPointer(clientX, clientY, offset),
          measureDetachedChat(),
          { width: window.innerWidth, height: window.innerHeight },
          CHAT_DETACH_EDGE_INSET_PX,
        ),
      );
    },
    onDragEnd: () => {
      chatDetachGrabOffsetRef.current = null;
    },
    measureGrabOffset: (e) => {
      const rect = detachedChatRootRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
  });

  // window resize で detach 窓が画面外に取り残されたら引き戻す
  // (= PreviewOverlay と同じ. detach 中のみ監視).
  useEffect(() => {
    if (typeof window === 'undefined' || !chatDetached) return;
    const onResize = (): void => clampDetachedChatIntoView();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [chatDetached, clampDetachedChatIntoView]);

  // Phase 6.7 サブ task 後追い (consult 2026-05-07): ArgbSliderPanel の ARGB ⇄ HSB
  // toggle. 旧 `ColorMakeCanvas.java:467-490` (Meta+click) 踏襲、新版は右クリック /
  // Alt+クリック の 2 経路サポート (= スポイトと同じ流儀). pen.color (rgb24) は
  // mode に依存せず単一 source-of-truth で保持し、表示時のみ HSV 変換 / 編集時のみ
  // RGB 逆変換する.
  const [argbMode, setArgbMode] = useState<'argb' | 'hsb'>('argb');

  // 2026-05-31 layer-popup-visibility-toggle (`w3-classic-layers.md §2.2.1` +
  // §2.2.2 post-implementation 調整):
  // LayerPopup の可視 toggle. 初版 (§2.2.1) は `ArgbSliderPanel` 内に統合していたが、
  // §2.2.2 で **ToolPanel の Preview button 直下に移動** (= Preview / Layer の両
  // 「常時参照型 floating panel visibility 切替」を集約). state は ClassicLayout が
  // 単一 source-of-truth として持ち、localStorage に永続化 (= 既存 user prefs lane
  // に相乗り、新規 lane は作らない). default true (= 初見ユーザは Layer 機能の存在に
  // 必ず触れる).
  const layerPopupVisibilityStoreRef = useRef(createLayerPopupVisibilityStore());
  const [layerPopupVisible, setLayerPopupVisibleState] = useState<boolean>(() =>
    layerPopupVisibilityStoreRef.current.getInitial(),
  );
  // Setter 経由で「state 更新 + localStorage 書き込み」を 1 つの動作に集約する.
  const toggleLayerPopupVisible = useCallback((): void => {
    setLayerPopupVisibleState((prev) => {
      const next = !prev;
      layerPopupVisibilityStoreRef.current.set(next);
      return next;
    });
  }, []);

  // §9.7 Phase 2: 以下は全て共有コントローラ (useRoomChrome、props.chrome) へ移設した:
  //   - LayerPopup の 👁 表示状態 (layerVisibilityMap) + handleToggleLayerVisible
  //   - 消しゴム復帰用 lastNonEraserPen + handleToggleEraser
  //   - M-bucket (lastMarkerKind) + onMarkerBucketToggle (旧キーボード M、Stage X で撤去予定。
  //     2026-06-12 に M→S=サインペン (`onSelectPen('flat')`) 直接キー化でキーボード経路から外れた)
  //   - 太さ 2 バケット (drawPenWidths / eraserPenWidths / widthBuckets) + 実効 width 同期
  //   - activePenWidths / activeSelectedPenWidthIndex の解決
  //   - MASK 2 slot + maskTypeUI + slotsToMask 再構成
  //   - ペン専用モード (penOnlyMode) + 自動 ON
  //   - 筆圧 mapping (pressureMap) + currentPressure + onPressureChange
  // ClassicLayout はこれらを props.chrome から alias で読むだけ (= ストアのビュー)。
  // 挙動は ClassicLayout 原典と同一 (useRoomChrome は原典ロジックを共有化した複製)。

  // Phase 6.7 サブ task 後追い (consult 2026-05-07): /bellon roomin (= 入室
  // お知らせベル切替) の state. localStorage で永続化、再ログイン後も維持する.
  // - store / soundAdapter は **mount 時に 1 回だけ生成** (useMemo).
  //   AudioContext は user-gesture 経由で初回 unlock するため再生成しない.
  // - bellOnRoomin は state. /bellon コマンドで toggle、`room.member-joined`
  //   listener が `'on'` のときだけ beep する.
  // - localStorage への書き込みは setBellOnRoomin の wrapper で行う.
  const bellStore = useMemo(() => createBellOnRoominStore(), []);
  const bellSound = useMemo<BellSoundAdapter>(() => createBrowserBellSound(), []);
  const [bellOnRoomin, setBellOnRoominState] = useState<BellOnState>(() => bellStore.read());
  const setBellOnRoomin = useCallback(
    (next: BellOnState): void => {
      setBellOnRoominState(next);
      bellStore.write(next);
    },
    [bellStore],
  );
  // bellOnRoomin の最新値を ref で保持 (= transport.onEvent listener が古い
  // closure 値を参照しないよう). useEffect で同期する.
  const bellOnRoominRef = useRef<BellOnState>(bellOnRoomin);
  bellOnRoominRef.current = bellOnRoomin;

  // §9.7 Phase 2: 筆圧 mapping 設定 (pressureMap / currentPressure / onPressureChange) は
  // 共有コントローラ (chrome) へ移設 (= 上の destructure)。挙動は原典と同一。

  // Phase 6.7 サブ task 後追い: `room.member-joined` 受信時にベルを鳴らす.
  // - server-side broadcaster は originator (= 自分自身の join) を除外する
  //   ので、自 join では beep しない (= 仕様通り).
  // - 失敗 (AudioContext 不在 / 再生失敗) は silent. UI には toast を出さない.
  useEffect(() => {
    const off = transport.onEvent((event) => {
      if (event.t !== 'room.member-joined') return;
      if (bellOnRoominRef.current !== 'on') return;
      bellSound.play();
    });
    return off;
  }, [transport, bellSound]);

  // Phase 6.7 サブ task 後追い: ChatPanel への命令的アクセス用 ref.
  // MemberPanel ダブルクリック時に `chatPanelRef.current?.insertName(name)`
  // で chat input に名前を挿入する.
  const chatPanelRef = useRef<ChatPanelHandle | null>(null);
  const handleMemberDoubleClick = useCallback((displayName: string): void => {
    chatPanelRef.current?.insertName(displayName);
  }, []);

  // 2026-06-11 キーボードショートカット共有フック化: Ctrl/Cmd+Enter (発言小窓) /
  // V (左右反転) / Tab (UI hide) / B,S,E (ツール) / +,-,0 (zoom) の listener 群を
  // `useLayoutKeyboardShortcuts` に逐語抽出し、Classic/Modern で共有する。Classic の
  // 挙動は不変 (= 旧 inline listener を 1 呼び出しに置換しただけ)。判定・ガード
  // (IME / 入力欄フォーカス / 修飾キー / preventDefault / document vs window attach 先)
  // は全てフック内で原典を踏襲。Space hold pan は別フック (useSpaceHoldPan) で別管轄。
  //   - onOpenChatPopup: ChatPanel handle 経由で発言小窓を開く (ref は listener 内で読む)。
  //   - onToggleUiHidden: Tab で uiHidden をトグル (= canvas 集中モード)。
  //   - ツール / zoom / flip は共有コントローラ chrome.* を渡す (Classic と同一ソース)。
  //   - onUndo: conflict-aware Undo (2026-07-23 是正)。`edit.undo` の guardTextInput に
  //     よりチャット入力欄フォーカス中は発火しない (Modern と同じ registry ガード)。
  useLayoutKeyboardShortcuts(
    {
      onOpenChatPopup: () => chatPanelRef.current?.openFloatingPopup(),
      onToggleUiHidden: () => setUiHidden((v) => !v),
      onSelectBrush,
      onSelectSignpen: () => onSelectPen('flat'),
      onSelectEraserDirect,
      // CSP流 循環ショートカット (2026-08-21、既定キー未割当): 個別ペン直接選択 + タブの
      // 「最後に使ったペンへ」。Classic はスポイト持続モードの概念が無いので Modern の
      // ような setSpuitMode(false) ラップは不要 (onSelectPenRef/onSelectTabRef へ
      // そのまま渡す)。
      onSelectDot: () => onSelectPenRef('dot'),
      onSelectTone: () => onSelectPenRef('tone'),
      onSelectAirbrush: () => onSelectPenRef('airbrush'),
      onSelectCrayon: () => onSelectPenRef('crayon'),
      onSelectPastel: () => onSelectPenRef('pastel'),
      onSelectTexturePen: () => onSelectPenRef('texture'),
      onSelectTabLine: () => onSelectTabRef('line'),
      onSelectTabFill: () => onSelectTabRef('fill'),
      onSelectTabTexture: () => onSelectTabRef('texture'),
      onZoomIn: handleZoomIn,
      onZoomOut: handleZoomOut,
      onZoomReset: handleZoomReset,
      onZoomFit: applyFitZoom,
      onToggleFlip: handleToggleFlip,
      // キャンバス表示回転 (canvas-rotation 段取り 2): R = 回転ツール (ブラシ等を選ぶまで
      // 持続、解除は chrome の onSelect* が担う)。左右回転 / リセットは既定キー無し。
      onSelectRotateTool: chrome.onSelectRotateTool,
      onRotateLeft: chrome.handleRotateLeft,
      onRotateRight: chrome.handleRotateRight,
      onRotateReset: chrome.handleRotateReset,
      // `[` / `]` 太さ段階増減 (2026-10-01、Modern から拡張)。Classic ではスライダを
      // 1 段動かしたのと同じ = ToolPanel の slider drag と同じ 2 手 (選択中 cell の値
      // 書き換え + pen.width 追従)。刻みは Modern と共通の `stepPenWidth`。
      onWidthStepDown: () => {
        const next = stepPenWidth(pen.width, -1);
        onPenWidthValueChange(activeSelectedPenWidthIndex, next);
        onWidthChange(next);
      },
      onWidthStepUp: () => {
        const next = stepPenWidth(pen.width, 1);
        onPenWidthValueChange(activeSelectedPenWidthIndex, next);
        onWidthChange(next);
      },
      onOpenShortcutSettings: () => setShortcutsOpen(true),
      onUndo: undo.triggerUndo,
    },
    {
      // 循環アルゴリズムの現在位置判定に使う (shortcutCycle.ts の penRef 一致判定)。
      currentPen: pen.pen,
      rememberedIndexByBinding: shortcutCyclePositions,
      onRememberedIndexChange: onShortcutCycleIndexChange,
    },
  );

  // §9.7 Phase 2: drawing engine + canvas ref、NameLabel/zoom/flipX ミラー、名表示
  // toggle、反転 toggle (handleToggleFlip)、zoom +/- ハンドラは共有コントローラ
  // (useRoomChrome、props.chrome) へ移設。canvas 本体と overlay は CanvasStage が
  // 永続描画する。pen / zoom / nameLabelMode / flipX 等は chrome から alias で読む
  // (= 上の destructure)。canvasRef はプレビュー/キャプチャで使うので chrome.canvasRef
  // を読む。

  // 2026-06-11 素の「V」キー (左右反転) / Tab (UI hide) / P,B,M,E (ツール) /
  // +,-,0 (zoom) の listener は `useLayoutKeyboardShortcuts` へ集約済 (= 上の
  // 共有フック呼び出し)。Classic 原典の inline listener はここに重複させない。

  // 2026-05-15 → 2026-06-12 共有化: 全画面 toggle (Fullscreen API)。state
  // (isFullscreen / fullscreenSupported) + fullscreenchange listener は Classic /
  // Modern 共通の `useFullscreen` フックへ切り出した (= 両レイアウトのヘッダで同じ
  // 「全画面」項目を出すため)。Classic 固有の iPad 注意モーダル経路 (下記
  // handleToggleFullscreen) はここに残す (= 挙動不変)。`toggle` は Modern が使う
  // 警告なし版なので Classic では参照しない。
  const { isFullscreen, supported: fullscreenSupported } = useFullscreen();

  // 2026-06-02 iPad Safari: 入室 (= room 表示) 時のスクロール対策。
  // (1) ログイン/名前入力画面でキーボード等により縦スクロールした位置が残ったまま
  //     入室すると room が下にズレ、上端の全画面ボタンが画面外に隠れる
  //     (ユーザー報告 2026-06-02) → mount 時に scrollY を 0 へ戻す。
  // (2) room 中は body を position:fixed (= `oekaki-room-scroll-lock` class) で
  //     完全ロックし、縦ジェスチャが document スクロールに化けないようにする。
  //     iOS Safari は `overflow:hidden` だけでは document スクロールを止めないため
  //     (= 端スライドで scroll できてしまう実機挙動)。これは Fullscreen API が縦
  //     スクロールを合図に全画面を解除する温床も断つ狙いを兼ねる。unmount (= 退室)
  //     で class を外し、ログイン等の他画面の通常スクロールを戻す。
  useEffect(() => {
    if (typeof window === 'undefined') return;
    document.body.classList.add('oekaki-room-scroll-lock');
    // ログイン/名前入力の input に focus が残るとキーボードが visual viewport を
    // 押し上げ、入室直後に画面がズレて見える。focus を外してキーボードを閉じる。
    (document.activeElement as HTMLElement | null)?.blur?.();

    // 2026-06-02 iPad Safari の visualViewport オフセット補正 (実測 sdbg で確定):
    // Safari のツールバーで visual viewport が layout viewport から下に offsetTop
    // (実測 25px) ずれ、高さも縮む。room を position:fixed で scroll ロックすると
    // ツールバーが畳まれず offsetTop が残り続け、固定枠の上端 (= 全画面ボタンの帯)
    // がツールバー裏に隠れる。visualViewport の offsetTop / height を CSS 変数に流し、
    // `body.oekaki-room-scroll-lock` の top/height をそこに合わせて相殺する。
    const root = document.documentElement;
    const vv = window.visualViewport;
    const syncViewport = (): void => {
      if (!vv) return;
      root.style.setProperty('--app-vvtop', `${vv.offsetTop}px`);
      root.style.setProperty('--app-vvh', `${vv.height}px`);
    };
    syncViewport();
    vv?.addEventListener('resize', syncViewport);
    vv?.addEventListener('scroll', syncViewport);

    // scroll リセットは mount 直後だと room レイアウト確定前で効かないことがある
    // (= canvas/engine 初期化で高さが動く)。即時 + 次フレームの 2 回打って確実化。
    const reset = (): void => window.scrollTo(0, 0);
    reset();
    const raf = requestAnimationFrame(() => {
      reset();
      syncViewport();
    });
    return () => {
      cancelAnimationFrame(raf);
      vv?.removeEventListener('resize', syncViewport);
      vv?.removeEventListener('scroll', syncViewport);
      root.style.removeProperty('--app-vvtop');
      root.style.removeProperty('--app-vvh');
      document.body.classList.remove('oekaki-room-scroll-lock');
    };
  }, []);

  // 2026-06-02 iPad 全画面注意モーダル (consult 2026-06-02):
  // iPad / iPadOS は Safari 仕様で「画面を下にスワイプすると Fullscreen API の
  // 全画面が解除される」. OS 挙動で Web 側から抑止できないため、全画面ボタンを
  // 押した人にだけ「スワイプで解除される」注意を出す据え置き対応.
  // - 対象は iOS / iPadOS のみ (= isTouchAppleDevice). デスクトップ (Mac/Win) は
  //   Fullscreen API が正常なので警告を出さず従来どおり即トグル.
  // - **一度でも全画面に入るまでは押すたび毎回出す** (consult 2026-06-02 追補):
  //   「やめておく」でキャンセルした人は全画面の挙動を体験していないので、次に
  //   押したらまた出す. 一度全画面に入った (= 挙動を理解した) ら以後は出さない.
  //   フラグは「表示済」ではなく「全画面に入った」で立てる (enterFullscreen 成功時).
  // - **session 限り・localStorage 不使用**. useState なので入室 (= ClassicLayout
  //   remount) でリセットされ、毎入室で「全画面に入るまで毎回」に戻る.
  // - 「入る時」だけ (= document.fullscreenElement === null). 既に全画面で「出る」
  //   操作は警告不要、即 exitFullscreen.
  // - 文面確認 (ネイティブ alert/confirm は iPad で全画面解除されるため禁止 →
  //   既存 ConfirmDialog を再利用). open は単純 boolean.
  const [fullscreenEnteredThisSession, setFullscreenEnteredThisSession] = useState<boolean>(false);
  const [fullscreenWarnOpen, setFullscreenWarnOpen] = useState<boolean>(false);

  // 実際に全画面化を実行する (= ボタン本体 / 注意モーダルの「全画面にする」から呼ぶ).
  // 失敗は ignore (Permission denied / browser policy). 成功時のみ
  // fullscreenEnteredThisSession を立て、以後この session では警告を出さない.
  // isFullscreen state 自体は fullscreenchange で同期されるので明示更新は不要.
  const enterFullscreen = useCallback((): void => {
    if (typeof document === 'undefined') return;
    void document.documentElement.requestFullscreen().then(
      () => setFullscreenEnteredThisSession(true),
      () => undefined,
    );
  }, []);

  const handleToggleFullscreen = (): void => {
    if (typeof document === 'undefined') return;
    if (document.fullscreenElement === null) {
      // 入ろうとしている. iOS/iPadOS かつ「まだ一度も全画面に入っていない」なら
      // 注意モーダルを出す (= ユーザが承知の上で onConfirm から requestFullscreen).
      // それ以外 (デスクトップ / 既に一度全画面に入った後) は警告なしで即全画面化.
      const isAppleTouch =
        typeof navigator !== 'undefined' &&
        isTouchAppleDevice({
          userAgent: navigator.userAgent,
          maxTouchPoints: navigator.maxTouchPoints,
          platform: navigator.platform,
        });
      if (fullscreenSupported && isAppleTouch && !fullscreenEnteredThisSession) {
        setFullscreenWarnOpen(true);
        return;
      }
      enterFullscreen();
    } else {
      // 既に全画面 → 出る. 警告不要、即 exit.
      void document.exitFullscreen().catch(() => undefined);
    }
  };

  // 2026-05-15: Tab キーで UI 全 hide (= キャンバスのみ集中モード). CSP /
  // Photoshop / SAI 流儀. 復帰も Tab で. input / textarea / contenteditable に
  // focus 時は通常の Tab 動作 (= focus 移動 / 文字入力) を維持して衝突回避.
  const [uiHidden, setUiHidden] = useState<boolean>(false);

  // 2026-06-13 sidebar 越境 fix (classic-sidebar-compaction.md §8): 横 splitter
  // の left (= 左カラム右端 + gap 8px) の実測値. 左カラム列が minmax(下限,
  // max-content) で中身より広がりうるため定数式では追従できない. この left は
  // 「splitter の drag 帯 (z-10 absolute) が左カラムのボタン (例: 最下段
  // [レイヤー] toggle) に被って click を奪う」regression (2026-05-31 stage-Y fix,
  // e2e layerPopupVisibilityToggle.spec.ts) の再発防止なので、必ず左カラム右端
  // より右に置く. 実測は左カラム wrapper の getBoundingClientRect().right を
  // grid 親左端基準に変換 + gap 8px. ResizeObserver は CSS zoom 変更では fire
  // しない (= 要素の local 座標サイズは不変) ため、sidebarZoom / uiHidden /
  // chatDetached を deps に含めて再実測する (= content 変化は RO が拾う).
  const [splitterLeftPx, setSplitterLeftPx] = useState<number | null>(null);
  // useLayoutEffect: mount / 再表示の最初の paint 前に実測を済ませ、fallback 値
  // (= 旧定数式) で 1 frame だけ帯が左カラムに被るのを避ける.
  useLayoutEffect(() => {
    // deps 変化での再実測トリガ (上記コメント参照). 計算自体は DOM 実測のみ.
    void sidebarZoom;
    void uiHidden;
    void chatDetached;
    void sidebarDockSide;
    const parent = gridParentRef.current;
    if (parent === null) return undefined;
    // §12 (floating-panels-2026-08-01.md): サイドバーがフロート中は grid 列
    // から左カラムのトラック自体が無くなる (= 詰まる) ので、splitter は grid
    // 左端 (padding 分だけ) から始まってよい。leftColRef は floating 中も
    // 同じノードを指すが、そのとき返る rect は position:fixed の浮遊位置で
    // あって「ドック列の右端」ではないため測ってはいけない。
    if (sidebarFloatOpen) {
      setSplitterLeftPx(0);
      return undefined;
    }
    const leftCol = leftColRef.current;
    if (leftCol === null) return undefined;
    const measure = (): void => {
      const rect = leftCol.getBoundingClientRect();
      // uiHidden (= `hidden` class) 中は rect が 0 になる. splitter 自体も
      // 非表示なので、直前の実測値を保持して 0 起点の誤値を書かない.
      if (rect.width <= 0) return;
      const parentRect = parent.getBoundingClientRect();
      // 2026-08-01 追加依頼: 右ドック対応。サイドバーが右側にいるときは、
      // splitter が避けるべき辺は「grid 親の右端からサイドバー左端までの距離」
      // (= 右ドック側の inset)。左ドック時と対称の式なだけで、判定ロジックは
      // 増えていない (下の JSX 側で left/right どちらの inset として使うかを
      // 出し分けるだけ)。
      setSplitterLeftPx(
        sidebarDockSide === 'right'
          ? parentRect.right - rect.left + 8
          : rect.right - parentRect.left + 8,
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(leftCol);
    return () => ro.disconnect();
  }, [sidebarZoom, uiHidden, chatDetached, sidebarFloatOpen, sidebarDockSide]);

  // 2026-05-15: Space hold 中の手のひらモード (CSP / Photoshop / SAI 流儀).
  // - Space 押下中だけ一時的に panning 化、放したら自動復帰.
  // - 2026-09-29 viewport transform 移行 (決定5) 以降は CanvasStage が
  //   `chrome.setViewport` (= `{tx,ty}` 座標変換) でドラッグ pan を行う (旧
  //   canvas wrapper の overflow-auto + programmatic scrollLeft/scrollTop は廃止)。
  // - panning 中は inner wrapper に `pointerEvents: 'none'` を当てて drawing engine
  //   への pointer event を遮断 (= 既に start 済の stroke は engine 側で完結).
  // - cursor は isPanning ? (isPanActive ? 'grabbing' : 'grab') : default.
  // - input/textarea/contenteditable focus 時は Space を通常の文字入力として扱う.
  //
  // 2026-05-17 iPad scroll 救済 (consult 2026-05-17): pan mode の入力経路を 2 ソース化.
  // - `spaceHeld`: Space hold 中フラグ (= 旧 isPanning state を rename). 既存の
  //   keydown/keyup ハンドラで on/off、放したら必ず off に戻る一時的フラグ.
  // - `panToggleOn`: ツールバー「手のひら」チェックボックスによる持続トグル.
  //   iPad ユーザ救済用 (touch-action:none で 2本指 scroll が封じられ、外付け
  //   キーボード不在で Space hold 経路も使えないため). チェック ON の間は ESC や
  //   Space 解除では消えず、明示的にチェック OFF するまで pan モードが続く.
  // - `isPanning` は両者の OR (= 派生値). handler / cursor CSS / pointerEvents
  //   切替の判定はすべてこれを参照する.
  // §9.7 Phase 2: pan モードの 2 入力ソース (Space hold / ツールバートグル) と派生
  // `isPanning` は ClassicLayout が持つ (= ツールバー UI / キーボード listener はここ)。
  // 実際のドラッグ pan (= 旧 onCanvasWrapperPointer* + cursor grab/grabbing +
  // pointerEvents:none + cleanup、2026-09-29 決定5以降は tx/ty transform 方式) は
  // 表示枠 (`<main>`) を所有する CanvasStage へ移設し、ここでは `isPanning` を
  // CanvasStage に panActive として渡すだけ。canvasScrollRef は RoomLayout 所有の
  // prop (CanvasStage と共有) で、プレビュー/imagepaste の viewport 計算で読む。
  const [spaceHeld, setSpaceHeld] = useState<boolean>(false);
  const [panToggleOn, setPanToggleOn] = useState<boolean>(false);
  const isPanning = spaceHeld || panToggleOn;
  // §9.7 Phase 2: pan の有効状態を共有コントローラへ sync (= CanvasStage が読んで
  // ドラッグ pan を行う)。Space hold / トグルの OR を単一値で stage に渡す。
  useEffect(() => {
    setPanActive(isPanning);
  }, [isPanning, setPanActive]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.code !== 'Space') return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      if (isTextInputFocus(e.target)) return;
      // page scroll 抑止 (= Space は browser default で scroll down).
      // 2026-05-16 fb: `e.repeat` の early-return が preventDefault より前にあると
      // 長押し中の auto-repeat keydown (= e.repeat === true) が抜けてしまい、browser
      // が canvas wrapper (overflow-auto) を scroll down し続け「canvas が下に張り付く」
      // 体感バグになる. preventDefault を先に呼び、state 更新だけを repeat skip する.
      e.preventDefault();
      // repeat でも立てる (idempotent): 別アプリ側で押し始めた Space は、絵チャに届く最初の
      // keydown が repeat=true になる (実機報告 fix/space-pan-window-activation)。
      setSpaceHeld(true);
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      if (e.code !== 'Space') return;
      // input focus 中でも release は処理 (= 落ちなくする). フラグ自体は input
      // focus 時に立たないので setSpaceHeld(false) は冪等で安全.
      // panToggleOn は触らない (= Space hold の有無と独立した持続トグル).
      // toggle ON が残っていれば isPanning は引き続き true → pan アクティブな
      // ドラッグは継続される. toggle OFF かつ Space 解除のときだけ完全 cleanup
      // が後続の useEffect で走る.
      setSpaceHeld(false);
    };
    // 押しっぱなし誤検知を防ぐ cleanup (2026-09-30、Modern の useSpaceHoldPan と揃える):
    // Space を押したまま別ウィンドウへ移る / タブが hidden になると keyup を取りこぼし
    // spaceHeld が立ちっぱなしになる (= 戻ってきても pan モードが残る)。必ず false に戻す。
    const onBlur = (): void => setSpaceHeld(false);
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') setSpaceHeld(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // 2026-06-11: Tab (UI hide) / P,B,M,E (ペン種) / +,-,0 (zoom) のキーボード
  // ショートカット listener は `useLayoutKeyboardShortcuts` へ集約済 (= 上の共有
  // フック呼び出し)。挙動・ガードは原典踏襲。
  // §9.7 Phase 2: zoom +/- / reset ハンドラ本体は共有コントローラ (chrome.handleZoomIn /
  // handleZoomOut / handleZoomReset)、CSP 流儀のペン種ショートカットの分岐 (P/B brush /
  // M marker bucket / E eraser) も共有フックへ。α-feedback K の zoom-scroll 補正本体は
  // 共有 useRoomChrome + CanvasStage へ移植済 (= Classic/Modern 両方に効く)。

  // §9.7 Phase 2: engine 接続 (= ready コールバック → SessionProvider への登録)、全消し
  // 判定/handler、canImagepaste は共有コントローラ (chrome) へ移設。canvas/overlay は
  // CanvasStage が永続描画し、engine 登録は CanvasStage 内 DrawingCanvas が 1 回だけ行う
  // (= 切替を跨いで 1 回 → engineRef が null にならず desync 根絶)。
  // Phase 6.8-A Task 2: キャプ (camera) click handler. canvasRef 経由で
  // canvas の PNG dataURL を生成し, programmatic `<a download>` で保存させる.
  // - dataURL を `<a href>` に直接乗せて click() する標準パターン (URL.create
  //   ObjectURL 経路は MVP 範囲では不要).
  // - ファイル名は `oekaki-${YYYYMMDD-HHmmss}.png` (ローカル時刻).
  // - canvas が未 mount のとき (= engine 未準備) は何もしない (defensive).
  // - Safari iOS 16+ の動作は実機検証が必要 (PR レベルでは note のみで OK).
  // W3 Classic レイヤー追加 (consult 2026-05-26): キャプチャは **全層含める**
  // (= 👁 で隠した層もキャプチャに残す, docs/_wip/w3-classic-layers.md §2.3).
  // 表示 canvas (`canvasRef.toDataURL`) は visibility-aware (= 隠した層が抜ける)
  // ので、engine の全層 export 経路 (`exportAllLayersBlob`, visibility 無視で
  // 全層含む) を優先して使う. engine 未準備 / 当該メソッド未提供 (worker 経路の
  // 全層 export plumbing が未着手) の場合は表示 canvas にフォールバックする
  // (= キャプチャ機能自体は常に動く).
  // §9.7 Phase 2: 以下は全て共有コントローラ (useRoomChrome、props.chrome) へ移設した
  // (= 上の destructure で alias 取得)。挙動は ClassicLayout 原典と同一:
  //   - triggerDownload / handleCapture (全層 export → 表示 canvas fallback)
  //   - handleSelectActiveLayer / handleToggleLayerVisible (LayerPopup 配線)
  //   - imagepaste (useImagepaste、viewport-center 算出も chrome 側で canvasScrollRef 経由)
  //   - onSelectPen / paletteSlots / onColorChange / onPaletteSlotSelect / onWidthChange /
  //     onAlphaChange / handleToggleEraser / onPenWidthValueChange / onPenWidthCellClick
  //   - onMaskSlotClick / onMaskTypeToggle (MASK 2 slot + type)

  // 2026-05-27 省スペース化: 上部バーに出すルーム名ラベル (旧サイドバー最下部の
  // `<p>` から移設). 空なら room UUID 先頭 8 桁にフォールバック (旧挙動踏襲).
  const inviteDialog = useInviteDialog(state.roomId);
  const whatsNew = useWhatsNew(undefined, undefined, {
    ...(whatsNewAutoOpen !== undefined ? { autoOpen: whatsNewAutoOpen } : {}),
    ...(onWhatsNewAutoOpened !== undefined ? { onAutoOpened: onWhatsNewAutoOpened } : {}),
  });
  const roomLabel = state.roomName || state.roomId.slice(0, 8);
  // 2026-05-27 省スペース化 (classic-sidebar-compaction.md §2.2): 上部バーに載せる
  // 項目を **データとして** 定義する (Modern メニューバーの前身、presentation 差し
  // 替え可能化). ここでは語彙 (全画面 / 表示 / ルーム設定 / 退室) を配列で組み立て、
  // 描画は下の TopBar component が担う. 全画面は API 不在環境では項目自体を出さない.
  //
  // 2026-05-27 上部バー再編 (consult 確定): 「自分用＝左 / 管理＝右、頻度順、owner は
  // 状態依存アクセント」に並べ替える.
  //   非オーナー: [取り消し][全画面][表示][ペン][ショートカット] …room name… [🔒ルーム設定][退室]
  //   オーナー:   [取り消し][全画面][表示][ペン][ショートカット] …room name… [ルーム設定*][退室]   *=琥珀アクセント
  // - 左クラスタ (align 既定 start): **取り消し (Undo) が先頭** (2026-07-23 追補) →
  //   全画面 (頻用) → 表示 (set-once) → ペン (2026-06-13 続報) → ショートカット
  //   (= 個人設定モーダル群の並び).
  //   左クラスタは既存コメントどおり「自分用・頻度順」の並びだが、他の項目
  //   (表示/ペン/ショートカット等) は全て設定モーダルの入口 (= 開いて初めて効果が
  //   出る) であるのに対し、Undo は**描画操作そのもの** (押した瞬間に直近の
  //   ストロークが消える) で最も高頻度に押される. 頻度順の趣旨に最も忠実なのは
  //   先頭であり、全画面より前に置く.
  //   全画面は fullscreenSupported 条件付き spread のまま、配列上 表示 より前に置く.
  // - 右クラスタ (align: 'end'): ルーム設定 → 退室 の順 (TopBar は endItems を配列順
  //   で描画). ルーム設定は scope:'owner' で owner 判定 (isOwner) に応じて TopBarButton
  //   が控えめ (🔒) ⇄ 琥珀アクセントを切替える. 退室は据え置き (位置・色・挙動不変).
  const topBarItems: ReadonlyArray<RoomHeaderItem> = [
    // conflict-aware Undo (2026-07-23 追補): 上部バー常設ボタン。Modern
    // (ModernCommandCluster) は aria-label「元に戻す (Undo)」/ title「元に戻す
    // (Undo) — Ctrl+Z / Cmd+Z」だが、Classic の上部バーには**既に**全画面解除
    // ボタンが `isFullscreen ? '元に戻す' : '全画面'` でラベル「元に戻す」を使って
    // いる (すぐ下の 'fullscreen' 項目) ため、そのまま揃えると全画面時にラベルが
    // 衝突する (誤タップで別の意味の操作と誤認されうる)。可視ラベルは「取り消し」
    // に変え、Undo であることは title (tooltip) で明示して Modern と実質同義を
    // 保つ (= 表現パリティは「見た目の文言を一言一句揃える」ではなく「同じ機能に
    // 迷わず到達できる」ことと解釈. ラベル衝突を残したまま揃えるより優先).
    // disabled は cancel 可能な stroke が無いとき (undo.canUndo === false)。
    // badge (2026-07-23 追補): 初回 Undo 実行 (成功/拒否問わず) まで「New!」を表示
    // (undo-conflict-aware-2026-07-12.md、undoBadgeSeen.ts — Classic/Modern 共有の
    // 1 本の localStorage key。Modern 側で消しても連動して消える)。
    {
      kind: 'action',
      id: 'undo',
      label: '取り消し',
      onClick: undo.triggerUndo,
      disabled: !undo.canUndo,
      title: connectionLocked
        ? '元に戻す (Undo) — つながったら使えます'
        : '元に戻す (Undo) — Ctrl+Z / Cmd+Z',
      scope: 'session',
      badge: undo.showBadge,
      manualId: 'draw.undo',
    },
    ...(fullscreenSupported
      ? ([
          {
            kind: 'action',
            id: 'fullscreen',
            label: isFullscreen ? '元に戻す' : '全画面',
            onClick: handleToggleFullscreen,
            pressed: isFullscreen,
            scope: 'session',
            manualId: 'view.fullscreen',
          },
        ] as const)
      : []),
    {
      kind: 'action',
      id: 'view-prefs',
      label: '表示',
      onClick: () => setUiPrefsOpen(true),
      title: '表示の設定 (サイドバー / チャット欄の大きさ)',
      opensDialog: true,
      scope: 'personal',
      manualId: 'layout.ui-scale',
    },
    // 作者 staging フィードバック 2026-09-19 follow-up: ツールパネルにあった
    // 「カーソル」トグルを上部バーへ移動 (= 「表示」の隣、発見性の高い常設位置)。
    // RoomHeader の項目データは文字ラベルのみの action か任意ノードの custom
    // しかなく、自分のサムネ (§A-3) を乗せるため custom で `CursorHeaderButton`
    // (RoomHeaderButton と同じ見た目を手動複製) を渡す。
    {
      kind: 'custom',
      id: 'cursor-editor',
      node: (
        <CursorHeaderButton
          open={cursorEditorOpen}
          onToggle={toggleCursorEditor}
          selfCursor={selfCursor}
          showBadge={!cursorNewBadgeSeen}
        />
      ),
    },
    {
      // 2026-06-13 続報 (consult): 描画ツール挙動の設定。「表示」の隣に独立
      // ボタンを出す (= ショートカット設定の前例踏襲)。ToolPanel は chrome 凍結
      // (classic-sidebar-compaction.md §7) のため設定系モディファイアの受け皿は
      // この専用モーダル (modern-layout.md §1.1 表現パリティ)。
      // 2026-06-27 (consult): 手ぶれ補正 / AA に加えブラシカーソル / スポイトの取得元を
      // 束ねるため「ペン」→「描画」へ改名 (= 旧「表示」に混在していた描画系を集約)。
      kind: 'action',
      id: 'pen-settings',
      label: '描画',
      onClick: () => setPenSettingsOpen(true),
      title: '描画の設定 (手ぶれ補正 / アンチエイリアス / ブラシカーソル / スポイトの取得元)',
      opensDialog: true,
      scope: 'personal',
    },
    {
      // 2026-06-12 ショートカット設定 (shortcut-settings.md スライス2): 「表示」の隣に
      // 独立ボタンを出す (発見性フェーズ)。`?` ホットキーでも同モーダルを開く。
      kind: 'action',
      id: 'shortcut-settings',
      label: 'ショートカット',
      onClick: () => setShortcutsOpen(true),
      title: 'ショートカットキーの一覧・変更 (?)',
      opensDialog: true,
      scope: 'personal',
      manualId: 'shortcut.list',
    },
    // T3 絵ログ (タイムラプス) X 共有 (growth-timelapse-share-2026-07-02.md §5.3.8):
    // 左クラスタ内、ショートカットの隣。support gate (timelapseSupported = WebCodecs
    // 非対応環境では非表示) を fullscreen と同じ条件付き spread で表現する。UI 表記は
    // §5.3.7 で「タイムラプス」→「絵ログ」に変更済み (内部 id は timelapse-share 据え置き)。
    // 2026-07-08 方針変更 (room-settings 流儀): 絵ログ入口は **全員に見せて**、非オーナーが
    // 押したらオーナーログインへ誘導する (履歴 DL は owner 認証必須のままなので同意なし公開は
    // 抑止される)。gate は support/flag のみ (isOwner を外す)。tooltip は owner 状態で出し分け、
    // 非オーナーには「オーナーだけが絵ログを共有できます。オーナーはこちらからログイン」と案内。
    // 初回は「New!」バッジ (timelapseSeen が false のとき) を付ける。
    ...(TIMELAPSE_SHARE_ENABLED && timelapseSupported
      ? ([
          {
            kind: 'action',
            id: 'timelapse-share',
            label: '絵ログ',
            onClick: handleTimelapseEntryClick,
            title: isOwner
              ? '絵ログを共有'
              : 'オーナーだけが絵ログを共有できます。オーナーはこちらからログイン',
            opensDialog: true,
            scope: 'personal',
            badge: !timelapseSeen,
            // 非オーナーは 🔒 でロックを示唆 (琥珀昇格はしない、New! を埋もれさせない).
            // owner 認証すると即 false になり 🔒 が消える (isOwner 追従).
            locked: !isOwner,
            manualId: 'keep.timelapse',
          },
        ] as const)
      : []),
    // 過去キャンバス入口 (Slice 2)。絵ログの隣、全員表示 (support gate は不要 = 一覧
    // endpoint は署名に依存せず常時 mount)。非オーナーが押すとルーム設定 (ログイン誘導)
    // へ、オーナーは一覧モーダルへ。tooltip / New! バッジは絵ログと同じ流儀で出し分ける。
    {
      kind: 'action',
      id: 'past-canvas',
      label: '過去キャンバス',
      onClick: handlePastCanvasEntryClick,
      title: isOwner
        ? '全消しした過去のキャンバスを見る'
        : 'オーナーだけが過去キャンバスを見られます。オーナーはこちらからログイン',
      opensDialog: true,
      scope: 'personal',
      badge: !pastCanvasSeen,
      // 絵ログと同じ: 非オーナーは 🔒 でロックを示唆 (琥珀昇格なし、isOwner 追従).
      locked: !isOwner,
      manualId: 'keep.past-canvas',
    },
    // 使い方マニュアル (作者決定 2026-09-22、目立たせ改修): 左寄せ群の**最後**
    // (= 「過去キャンバス」の右、部屋名の手前) に置く。別タブで www の
    // `/help/?env=classic` を開く。マニュアル項目としては扱わない (= data-manual を
    // 付けない、manualRegistry.ts の登録対象外)。他ボタンと違うティール系アクセント
    // (scope:'help') + "?" 文字プレフィクス (Classic はテキストボタンのため文字で
    // 目立たせる、Modern/スマホは丸囲み HelpIcon SVG) + 初回のみ「New!」バッジ。
    {
      kind: 'action',
      id: 'help',
      label: '? 使い方',
      onClick: handleHelpEntryClick,
      title: '使い方マニュアルを別タブで開く',
      scope: 'help',
      badge: !helpSeen,
    },
    // 新機能の紹介 (whats-new-2026-10-15.md): 自動では開かず、未読があるときだけ控えめな
    // ドットを付ける。使い方の隣 (= 「知る」系を並べる)。
    {
      kind: 'action',
      id: 'whats-new',
      label: '新機能',
      onClick: whatsNew.openDialog,
      title: '新機能の紹介を見る',
      opensDialog: true,
      unreadDot: whatsNew.hasUnread,
    },
    // 招待 (room-invite-2026-10-01.md): 部屋 URL の表示 / コピー / 共有 / QR。左寄せ群の
    // 最後 (= 部屋名の近く) に常設。
    {
      kind: 'action',
      id: 'invite',
      label: '友達を招待',
      onClick: inviteDialog.openDialog,
      title: '部屋の URL を友達に送る (コピー / 共有 / QR)',
      opensDialog: true,
      scope: 'invite',
    },
    {
      kind: 'action',
      id: 'room-settings',
      label: 'ルーム設定',
      onClick: () => setSettingsOpen(true),
      // owner 状態で tooltip を出し分け: 未認証は「オーナーはここからログイン」と
      // して一般ユーザに主張しない (= 別端末ログインの入口を兼ねるため隠せない).
      title: isOwner
        ? 'ルーム名・公開設定・オーナーパスワードなど'
        : 'オーナーはここからログイン (ルーム名・公開設定など)',
      opensDialog: true,
      align: 'end',
      scope: 'owner',
      manualId: 'room.settings',
    },
    {
      kind: 'action',
      id: 'leave',
      label: '退室',
      onClick: leave,
      align: 'end',
      title: 'ルームから退室',
      scope: 'session',
      manualId: 'start.leave',
    },
  ];

  return (
    <>
      {/* メンテ予告 / お知らせバナー (Phase 7 Task 7-6 / Phase 9.6) と入室お知らせモーダル
          は 2026-06-14 に RoomLayout (App.tsx) 直下へ hoist した (= Classic/Modern
          共有層)。以前は両 layout が各自所有していたため、layout 切替で chrome が remount
          → dismiss 状態 (memory only) がリセットされバナー/モーダルが復活していた。
          banner は RoomLayout root (relative h-full) を anchor に absolute top-* で出るので
          ここに wrapper を置かなくても見た目・stacking は不変。ここでは mount しない。 */}
      <div className="relative h-full">
        {/* R14: 2 行 x 3 列 grid. 左カラム row-span-2 / 上段 Canvas col-span-2 /
            下段 Chat (1fr) + Member (140px) が同 row 同高 180px.
            2026-05-15: uiHidden 時 (Tab toggle) は 1 行 1 列 grid に切替えて
        {/* R14: 2 行 x 3 列 grid. 左カラム row-span-2 / 上段 Canvas col-span-2 /
            下段 Chat (1fr) + Member (140px) が同 row 同高 180px.
            2026-05-15: uiHidden 時 (Tab toggle) は 1 行 1 列 grid に切替えて
            canvas のみ表示. col-span-2 はそのまま (= 1 列 grid で auto cap される)、
            左カラム / chat / member wrapper を `hidden` で消す. */}
        {/* 2026-05-17 個人 UI スケール (consult 2026-05-17) 追補:
            zoom を子 wrapper にだけ当てると grid 親の列幅 (= 60px 固定) に
            押し込められて zoom 拡大した中身が clip / overflow する. 左カラム
            列幅は sidebarZoom (= 1.0 / 1.1 / 1.25) でスケールさせる. 中央 canvas
            列 (`minmax(0,1fr)`) は scale しない (= canvas に副作用を出さない方針
            の継続). 子 wrapper の `zoom: sidebarZoom` はそのまま残し、box と
            中身が同じ倍率で拡縮される. uiHidden 時は 1 列 1 行に倒すので scale
            連動なし.
            連動値 (6 段 zoom = xxsmall .72 / xsmall .85 / small 1.0 / medium 1.1 /
            large 1.25 / xlarge 1.4):
              - 左カラム列幅 (60 * sidebarZoom):
                  ~43px / ~51px / 60px / 66px / 75px / 84px
            2026-06-13 iPad 越境 fix (classic-sidebar-compaction.md §8): 左カラム
            列は固定 px → `minmax(60 * sidebarZoom px, max-content)` に変更.
            外枠 fieldset (パレット + ツール, border 2px + p-1 込み) の最小幅は
            論理 67px で、固定 60px 列を全スケールで 7px はみ出していた (overflow
            visible で見た目は保つが gap 8px のうち 7px を常に食う). さらに WebKit
            は最小フォントサイズ床 9px を持ち、zoom < 1 では text-[10px] ラベルが
            物理 9px に床打ちされて中身の論理幅が膨張する (xxsmall=0.72 で
            9 / 0.72 = 12.5px 相当 = 1.25 倍). この 2 つが重なると xxsmall で
            はみ出しが gap 8px を突き破り、左カラムのボタンが中央 canvas 列に
            越境する (iPad Safari で実測). minmax の max-content で列を中身追従
            にして越境を根絶する (= 100% 時はキャンバスが ~7px 右に寄り真の
            8px gap になる、想定どおりの副作用).
            2026-05-31 chat-scale-cleanup: 右 member 列幅 / 下 chat 行高は
            既存 splitter (chatRowPx / memberColPx の drag) でユーザが直接調整
            できるため、旧「チャット欄の大きさ」(= wrapper zoom) を撤去. grid
            template には raw chatRowPx / memberColPx をそのまま流す (= zoom 倍掛け
            消滅、素直な実装に戻る). チャット文字サイズだけ別軸 chatFontScale
            (= ChatPanel に `fontScaleEm` prop で渡す) で扱う.
            2026-05-17 追補2 (個人 UI スケールの続き): memberColPx / chatRowPx
            は drag-resize splitter で 80..360 / 80..400 の raw px に動的に変えられる
            (= localStorage に永続化). splitter は relative grid 親に absolute 配置. */}
        <div
          ref={gridParentRef}
          // 2026-06-12: 上パディングを除去 (px-2 pb-2)。Modern はヘッダ(nav)がルート
          // 最上部に flush なので、Classic の上部バーも上マージン 0 に揃えて Classic⇄Modern
          // 切替時の縦ぶれを無くす (ユーザ指摘)。左右/下の 8px は維持。
          className={uiHidden ? 'grid h-full' : 'relative grid h-full gap-2 px-2 pb-2'}
          // 2026-05-27 省スペース化 (classic-sidebar-compaction.md §2.2): 最上段に
          // 全列をまたぐ帯 (RoomHeader) の row を新設し、表示 / ルーム設定 / 全画面 /
          // 退室 / ルーム名 を移設する. uiHidden / chatDetached の各分岐を壊さない
          // よう、3 分岐すべてで row template の先頭を `auto` (= RoomHeader の自然高
          // py-1.5) にするだけに留める (= uiHidden は canvas のみなので帯を出さず
          // 従来通り 1 行のまま). 2026-06-12: 固定 28px (旧 TOPBAR_PX) → auto に変更し
          // Modern の自然高ヘッダと帯高を一致させる (= 切替時の縦ぶれを無くす).
          // 中央 canvas / bottom row / 左カラムには明示の gridRow を当てて auto-
          // placement の取り違えを避ける (下の各 wrapper を参照).
          style={
            uiHidden
              ? {
                  gridTemplateColumns: 'minmax(0,1fr)',
                  gridTemplateRows: 'minmax(0,1fr)',
                }
              : {
                  // §12 (floating-panels-2026-08-01.md、方針変更 2026-08-01):
                  // サイドバーがフロート中は列テンプレートから左カラムのトラック
                  // 自体を除く (= 2 列: canvas + member)。col-span-2 の canvas/
                  // bottom-row は auto-placement で残り列を丸ごと占有するため、
                  // これだけで「元スロットが詰まってキャンバスが広がる」(§3) が
                  // 実現する (= 追加のロジック不要)。docked のときは従来どおり
                  // 3 列 (左カラム列は minmax(下限, max-content) で中身追従、
                  // 2026-06-13 越境 fix コメント参照)。2026-08-01 追加依頼:
                  // 右ドック時はサイドバー列を最後尾に回す (canvas/bottom-row は
                  // 明示 gridColumn を持たず col-span-2 auto-placement で残り列を
                  // 占有するだけなので、この列順の入替だけで canvas/member が
                  // 左に寄り、サイドバーが右端になる。追加の配置ロジックは不要)。
                  gridTemplateColumns: sidebarFloatOpen
                    ? `minmax(0,1fr) ${memberColPx}px`
                    : sidebarDockSide === 'right'
                      ? `minmax(0,1fr) ${memberColPx}px minmax(${60 * sidebarZoom}px, max-content)`
                      : `minmax(${60 * sidebarZoom}px, max-content) minmax(0,1fr) ${memberColPx}px`,
                  // 行は chatDetached で変わる (= chat+member を切り離すと bottom
                  // row が消えて canvas が画面下端まで延伸する、旧版
                  // `ChatPanel.java:1865 layoutWhiteboard()` 踏襲)。列 (sidebar
                  // float) と行 (chat detach) は独立な軸なので個別に分岐する。
                  gridTemplateRows: chatDetached
                    ? 'auto minmax(0,1fr)'
                    : `auto minmax(0,1fr) ${chatRowPx}px`,
                }
          }
        >
          {/* 2026-05-27 省スペース化 (classic-sidebar-compaction.md §2.2): 上部バー.
              全列をまたぐ薄い帯. uiHidden 時 (Tab toggle = canvas のみ集中モード) は
              他 chrome と一緒に消す. 項目はデータ配列 (topBarItems) から描画し、
              Modern では同じ配列をメニュー化できる (presentation 差し替え可能). */}
          {!uiHidden && (
            // 2026-06-08 Modern Layout shell (§9.4.1 ③): Classic ⇄ Modern 切替トグル.
            // App から流し込まれた slot (layoutToggle) を右端クラスタ手前に置く (= 退室の左隣).
            // 2026-06-12: 帯を共有 RoomHeader に抽出 (Classic / Modern 完全一致化)。grid 最上段
            // の row に置くため明示の gridColumn '1 / -1' + gridRow 1 を wrapper で当てる
            // (RoomHeader 自体は自然高 py-1.5 なので grid row は auto)。
            // biome-ignore format: smoke test が `<RoomHeader items={topBarItems}` を 1 行で固定するため改行しない.
            <div style={{ gridColumn: '1 / -1', gridRow: 1 }}>
              <RoomHeader items={topBarItems} roomLabel={roomLabel} ownerAuthenticated={isOwner} layoutToggle={layoutToggle} retentionChip={<RoomRetentionChip roomId={state.roomId} adminToken={adminTokens.get(state.roomId) ?? undefined} onOpenSettings={() => setSettingsOpen(true)} />} />
            </div>
          )}
          {/* Left column: palette + tools (in white box) / argb / mask / settings / leave.
              row-span-2 で 2 段全体に伸びる.
              2026-05-17 個人 UI スケール (consult 2026-05-17): CSS `zoom` で要素
              サイズ + 文字 + padding + gap を一括拡縮する. Tailwind の固定 px
              (60px / text-[10px] etc.) は書き換えず、wrapper の zoom で視覚的に
              拡大する. canvas wrapper には別途 `zoom: 1` を当てて pointer 座標
              計算への伝播を遮断する. */}
          <div
            // 2026-05-27 省スペース化 (classic-sidebar-compaction.md §2.2):
            // - 上部バー row を新設したので左カラムは row 2 から開始 (= grid-row
            //   `2 / -1` で chat 行まで縦に伸ばす). uiHidden 時は 1 行 grid なので
            //   `hidden` で消えるため row 指定は無視されて安全.
            // - overflow: §2.4 (2026-05-27) では縦溢れ保険の `overflow-y-auto` を
            //   撤去して visible に戻していた。理由は「CSS 仕様で `overflow-x` が
            //   `visible`→`auto` に暗黙格上げされ、縦スクロールバーが極狭 60px
            //   content 空間 (= 列幅 60 * sidebarZoom と同倍率 zoom) を食って横に
            //   潰れる / `overflow-x: clip` に倒すと content の右端が切れる」回帰を
            //   2 連続で出したため。
            //   **2026-08-12 B-1 で再導入する** (feedback-triage-2026-08-12.md §1)。
            //   §2.4 の撤去理由は次の 2 点で解消済み:
            //   (a) `.oekaki-hide-scrollbar` (styles.css) でスクロールバーの
            //       gutter を 0 にするので、§2.4 の主因「スクロールバーが幅を食う」
            //       が構造的に起きない。gutter が 0 なら横方向のはみ出し量も 0 な
            //       ので `overflow-x` の暗黙格上げも無害になる。
            //   (b) §2.4 当時の左カラム列は**固定 60px** で content が常に ~7px
            //       はみ出していた (= だから `overflow-x: clip` で右端が切れた)。
            //       2026-06-13 の iPad 越境 fix で列は
            //       `minmax(60 * sidebarZoom px, max-content)` = **中身追従**に
            //       変わっており、content が列幅を超えない (上の grid template の
            //       コメント参照)。切れる右端がもう存在しない。
            //   §2.4 の「compaction で縦は足りる」という前提自体が **iPad 横向きで
            //   崩れている**のが B-1 の報告 (= 色/ARGB より下が見切れ、スクロールも
            //   できない)。縦は素直にスクロールさせるのが正しい。
            //   floating 時は grid セルの高さ制約が無くなるので、代わりに
            //   `maxHeight` (visualViewport 基準、下の style を参照) で抑える。
            // §12 方針変更 (floating-panels-2026-08-01.md、2026-08-01):
            // このノードは docked (grid セル) と floating (position:fixed) の
            // **両方で使い回す** (= remount しない)。detach ドラッグの pointer
            // capture を跨いで維持するため、かつ chatDetached の既存パターン
            // (「wrapper の className だけ切替える」) を踏襲するため。
            // 2026-06-13 越境 fix: 横 splitter の left 実測用 ref (splitterLeftPx
            // effect 参照). zoom 込みの視覚幅は getBoundingClientRect で取れる.
            ref={leftColRef}
            data-testid={sidebarFloatOpen ? 'classic-sidebar-float' : undefined}
            // バグ修正 (2026-08-01 実機検証): `role="dialog"` にすると
            // `NO_SHELL_DRAG_SELECTOR` の `[role="dialog"]` 除外に**自分自身が
            // 一致**してしまい、`handleSidebarPointerDown` の `closest()` 判定が
            // 常に真になって以後の pointerdown を grip へ委譲しなくなる (= 最初の
            // detach ドラッグだけ動き、フロート後の再ドラッグ・別ジェスチャの
            // reposition が一切効かなくなる致命的回帰)。FloatShell.tsx と同じ理由
            // (非モーダルパネルは `role="region"` が適切) でここも `region` にする。
            {...(sidebarFloatOpen
              ? ({ role: 'region', 'aria-label': 'ツール (フロート表示中)' } as const)
              : {})}
            className={
              uiHidden
                ? 'hidden'
                : sidebarFloatOpen
                  ? `oekaki-hide-scrollbar fixed z-40 flex w-max flex-col gap-2 overflow-y-auto overscroll-contain bg-[var(--color-panel)] p-1 select-none ${outsetFrame} ${
                      sidebarNearDockEdge !== null && sidebarGrip.isPointerActive
                        ? 'ring-2 ring-[var(--color-accent)] opacity-70'
                        : ''
                    }`
                  : 'oekaki-hide-scrollbar flex min-h-0 flex-col gap-2 overflow-y-auto overscroll-contain'
            }
            style={
              sidebarFloatOpen
                ? {
                    zoom: sidebarZoom,
                    left: sidebarFloatPos.x,
                    top: sidebarFloatPos.y,
                    // 2026-08-12 B-1: floating 時は grid セルの高さ制約が外れるので、
                    // 代わりにビューポート高で頭打ちにする (= これが無いと iPad 横向き
                    // のような背の低い画面で下端が画面外に出たまま到達できない。
                    // 「動かしても見えない」= B-1 報告の後半そのもの)。
                    // - 基準は `--app-vvh` (= visualViewport.height、上の
                    //   syncViewport が流している)。iPad Safari はツールバーで
                    //   layout viewport と visual viewport がズレるため、`100dvh`
                    //   より visualViewport の方が実際に見えている高さに一致する。
                    //   room 表示中しか設定されないので `100dvh` を fallback に置く。
                    // - `/ sidebarZoom` で割るのは、この要素が `zoom` を持ち
                    //   **local 座標の length が zoom 倍で描画される**ため
                    //   (= 上の splitterLeftPx コメント「要素の local 座標サイズは
                    //   不変」の裏返し)。割らないと zoom > 1 で上限が zoom 倍に
                    //   緩み、頭打ちが効かない。
                    // - 位置 (top) は式に入れない: 高さが「ビューポート内に必ず
                    //   収まる」ことさえ保証すれば、既存の clamp (measureSidebar =
                    //   getBoundingClientRect の実測 = 視覚寸法) が常に画面内へ
                    //   引き戻せる。top を式に含めると zoom と fixed 座標の
                    //   相互作用に依存してしまうので避ける。
                    maxHeight: `calc((var(--app-vvh, 100dvh) - ${2 * TOOL_FLOAT_MARGIN}px) / ${sidebarZoom})`,
                  }
                : {
                    zoom: sidebarZoom,
                    gridColumn: sidebarDockSide === 'right' ? 3 : 1,
                    gridRow: '2 / -1',
                  }
            }
            // §12: シェル全体が掴み所 (= NO_SHELL_DRAG_SELECTOR に一致するボタン/
            // 入力/canvas 以外ならどこでも detach ドラッグを開始できる、新しい
            // 可視 chrome は増やさない)。docked/floating どちらでも同じハンドラ。
            onPointerDown={handleSidebarPointerDown}
            onPointerMove={sidebarGrip.onPointerMove}
            onPointerUp={sidebarGrip.onPointerUp}
            onPointerCancel={sidebarGrip.onPointerCancel}
            onLostPointerCapture={sidebarGrip.onLostPointerCapture}
          >
            {/* 常設グリップ帯 (2026-08-01 制約緩和・§1 の一点限定の例外、design memo
                floating-panels-2026-08-01.md に記録): 実機検証で「掴める場所が
                実質 3px しかない」ことが判明したため、掴み所を可視化する帯を
                docked/floating 両方で常時表示する (= §1「既定は現行配置のまま」の
                唯一の例外として旧版作者本人が明示許可、他の既定表示は不変)。
                Modern FloatShell の見た目はそのまま持ち込まず、Classic の意匠
                (LayerPopup/PenPopup/ColorWheelPalette と同じ配色 `bg-bg-soft` +
                `text-[10px] font-bold text-white`) に揃える。docked 時は他の
                Classic ボックスと同じ `outsetFrame` の自己完結した帯 (= 個々の
                白背景ボックスと並ぶ「もう1個の帯」として自然)、floating 時は
                従来通り親の `outsetFrame` に地続きの `border-b` (= 見た目は
                floating-only だった旧実装と同一、ラベル文言のみ変更)。
                高さは py-1 (実測で 20px 以上、real-machine 検証で確認)。
                ⇤ドック ボタンは floating 中のみ・位置ベース (sidebarRedockSide,
                §11 パターン) でラベル/遷移先が追従する。 */}
            <div
              data-testid="classic-sidebar-grip"
              // 2026-08-01 追加依頼: フロート時の幅をドック時 (73px) に近づけるため、
              // gap/padding も詰める (旧「⇤ドック」テキストボタン撤去だけでは
              // 126px→92px までしか縮まらなかった。帯の余白を切り詰めて更に接近させる)。
              className={`flex shrink-0 cursor-move items-center justify-between gap-1 bg-[var(--color-bg-soft)] px-1.5 py-1 text-[10px] font-bold text-white select-none ${
                sidebarFloatOpen ? '-m-1 mb-0 border-b border-black' : outsetFrame
              }`}
              style={{ touchAction: 'none' }}
            >
              <span className="flex min-w-0 items-center gap-1">
                <span aria-hidden="true">⠿</span>
                <span className="truncate">ツール</span>
              </span>
              {sidebarFloatOpen && (
                // 2026-08-01 追加依頼: 旧 Java 版パリティ (`PaintToolFrame.java:56`
                // `closeButton.setBounds(40, 3, 14, 14)` = MiniCloseButton、アイコンのみの
                // 極小ボタン)。旧「⇤ドック」テキストボタンは帯の min-content 幅がパネル
                // 全体を押し広げてしまい (フロート時 73px→126px の原因)、旧版と違い新版は
                // アイコンだけに削って幅を詰める (= 妥協ではなく旧版パリティ)。
                // aria-label/title は情報を落とさず現行のまま維持し、見た目 (border+bg 付き
                // 14px 角、旧版の 14×14 相当) だけ縮める。当たり判定は外側 <button> の
                // padding で確保する (見た目は小さいまま、タップ対象は広く保つ。
                // oekaki/CLAUDE.md のタッチ罠を踏まえ、視覚サイズと当たり判定サイズを
                // 意図的に分離する)。
                <button
                  type="button"
                  aria-label={`ツールを${sidebarRedockSide === 'left' ? '左' : '右'}のドックへ戻す`}
                  title={`${sidebarRedockSide === 'left' ? '左' : '右'}のドックへ戻す`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={handleCloseToolFloat}
                  className="flex shrink-0 items-center justify-center p-1"
                >
                  <span
                    aria-hidden="true"
                    className="flex h-3.5 w-3.5 items-center justify-center border border-black bg-white text-[10px] leading-none text-black"
                  >
                    {sidebarRedockSide === 'left' ? '⇤' : '⇥'}
                  </span>
                </button>
              )}
            </div>
            {/* 1. 白背景 outset frame ボックス: 16 色 + ペン種 + ペン幅 + 機能ボタン。
                §12: サイドバー全体 (本ボックス + ArgbSliderPanel + MaskPanel) が
                一体でフロートする (旧 Java 版 `paintToolFrame` パリティ、パネル
                単位でバラバラに浮くのではない)。ToolPanel も対象に含まれるため
                「ToolPanel は固定」方針は Classic では撤回した。 */}
            <fieldset
              className={`flex flex-col gap-1 bg-white p-1 text-black ${outsetFrame}`}
              aria-label="パレット + ツール"
            >
              <PalettePanel
                pen={pen}
                palette={paletteSlots}
                selectedIdx={selectedPaletteIdx}
                onSelectIdx={onPaletteSlotSelect}
                // controlled 化: dblclick された slot の色は単一クリック側
                // (`onSelectIdx` → `setPen({color: paletteSlots[idx]})`) で既に
                // pen.color に書き込まれており, palette 側の `value` にも流れて
                // いるので、ここでは「開く」だけで OK. 引数 `initial` は使わない.
                onColorDoubleClick={() => setColorWheelOpen(true)}
                onReset={onPaletteReset}
              />
              <ToolPanel
                pen={pen}
                onToggleEraser={handleToggleEraser}
                onWidthChange={onWidthChange}
                penWidths={activePenWidths}
                selectedPenWidthIndex={activeSelectedPenWidthIndex}
                onPenWidthCellClick={onPenWidthCellClick}
                onPenWidthValueChange={onPenWidthValueChange}
                onImagepasteFile={canImagepaste ? (file) => void imagepaste.start(file) : undefined}
                regionActive={chrome.regionToolActive}
                onSelectRegion={chrome.onSelectRegionTool}
                showRegionNewBadge={chrome.showRegionNewBadge}
                canClear={canClear}
                onClear={handleClear}
                onCapture={handleCapture}
                nameLabelMode={nameLabelMode}
                onToggleNameLabelMode={handleToggleNameLabelMode}
                zoom={zoom}
                zoomMin={ZOOM_FIT_MIN}
                zoomMax={ZOOM_MAX}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                // 2026-05-09 ペン専用モード (Palm Rejection、consult 2026-05-09).
                // checkbox の controlled value/onChange を直結.
                penOnlyMode={penOnlyMode}
                onPenOnlyModeChange={setPenOnlyMode}
                // 2026-05-17 iPad scroll 救済 (consult 2026-05-17): 手のひら
                // モード持続トグル. Space hold 経路と OR で `isPanning` を駆動する.
                panToggleOn={panToggleOn}
                onPanToggleChange={setPanToggleOn}
                // 2026-05-17 旧版 PreviewFrame 復活: キャンバス全体プレビュー
                // floating panel の開閉トグル.
                previewOpen={previewOpen}
                onTogglePreview={() => setPreviewOpen((p) => !p)}
                // 2026-05-31 layer-popup-visibility-toggle §2.2.2: LayerPopup の
                // visibility toggle. §2.2.1 で ArgbSliderPanel 内に統合していたが、
                // post-implementation 調整で ToolPanel の Preview button 直下へ
                // 移動 (= visibility toggle 群を集約).
                layerPopupVisible={layerPopupVisible}
                onToggleLayerPopup={toggleLayerPopupVisible}
                // 2026-06-07 キャンバス左右反転 (view flip、canvas-flip.md §4).
                flipX={flipX}
                onToggleFlip={handleToggleFlip}
                // 2026-06-20 ペン種選択をフロート「ペン」に集約 (consult 2026-06-20):
                // [プレビュ]/[レイヤー] 同型の [ペン] 入口ボタン → 常設フロート
                // 「ペン」(PenPopup) 開閉。入口は現在ペン種ラベルを併記しハイライト整合。
                penPopupOpen={penPopupOpen}
                onTogglePenPopup={togglePenPopup}
              />
              {/* 2026-05-09 筆圧設定 UI (consult / architecture.md §19.15):
                  ToolPanel ペン詳細セクション直下に「太さ筆圧」「濃さ筆圧」の
                  ON/OFF + min スライダを表示. ペン種に応じて表示する toggle が
                  切り替わる (= alpha pen で「太さ」を出すか等は PenPressureConfig
                  内部の `SHOW_*_TOGGLE` テーブルで決定). 値の永続化は
                  `pressureStore` 経由で localStorage. */}
              <PenPressureConfig
                penKind={pen.pen}
                settings={currentPressure}
                onChange={onPressureChange}
              />
            </fieldset>

            {/* 2. ARGB スライダ領域 (= 2026-05-31 layer-popup-visibility-toggle で
                outsetFrame fieldset 化). canvas (50x64) + `[A R G B]`/`[A H S V]`
                toggle の 2 要素を MaskPanel と同型の箱に統合
                (`w3-classic-layers.md §2.2.1` + §2.2.2 post-implementation 調整).
                旧独立 ARGB ⇄ HSB button + 旧 canvas 内 channel labels は撤去.
                `[レイヤー]` toggle は §2.2.2 で ToolPanel へ移動済. */}
            <ArgbSliderPanel
              pen={pen}
              onColorChange={onColorChange}
              onAlphaChange={onAlphaChange}
              mode={argbMode}
              onModeToggle={() => setArgbMode((m) => (m === 'argb' ? 'hsb' : 'argb'))}
            />

            {/* 3. MASK 領域 (R12: 旧版風 2 slot + 下部 toggle). */}
            <MaskPanel
              pen={pen}
              slot1={slot1}
              slot2={slot2}
              typeUI={maskTypeUI}
              onSlotClick={onMaskSlotClick}
              onTypeToggle={onMaskTypeToggle}
            />

            {/* 2026-05-27 省スペース化 (classic-sidebar-compaction.md §2.2):
                旧サイドバー最下部 (mt-auto) の 表示 / ルーム設定 / 全画面 / 退室 /
                ルーム名 ブロックは上部バー (TopBar) へ移設したため撤去した. */}
          </div>

          {/* §9.7 Phase 2: canvas 領域 = 空スロット div (計測フローティング・スロット方式)。
              canvas 本体・overlay・scroll コンテナ・pan・マスク枠・flipX 配線は CanvasStage が
              描画し、RoomLayout 直下 absolute でこのスロット矩形に重なる。ここはレイアウト上の
              「場所取り」だけを担う。grid 配置 (col-span-2 / gridRow 2 / uiHidden 分岐) と
              min-h-0/min-w-0 は **元の canvas wrapper と完全に同一** に保ち、Classic の grid
              幾何を不変にする (= CanvasStage はこの矩形に乗るので見た目が一致)。canvas の枠
              (outsetFrame の border・背景・p-2) は CanvasStage の <main> が frameBaseClassName
              経由で描く (= Classic の枠を渡して同一)。develop が canvasScrollRef へ inline 描画
              していた canvas + overlay 群 (DrawingCanvas / NameLabelOverlay / BrushCursorOverlay
              + flipX / pan) は CanvasStage 側へ移設済。 */}
          <div
            ref={canvasSlotRef}
            data-canvas-slot
            className="col-span-2 min-h-0 min-w-0"
            style={uiHidden ? undefined : { gridRow: 2 }}
          />

          {/* Bottom row: ChatPanel + MemberPanel を 1 つの wrapper に統合.
              2026-05-17 旧版 `■→` ボタン復活: `chatDetached === true` のとき
              wrapper を `position: fixed` + title bar 付き floating frame に変身、
              元の grid 内 bottom row は (grid-rows 切替で) 消える → canvas が下端
              まで延伸. それ以外の className 構造は維持し、内側の
              chat-member row は同じ JSX 位置 + 安定 key で remount を回避
              (= draft / 発言小窓 popup state / filteredUids など内部 state を保持).
              uiHidden (Tab toggle) は detach 状態に関係なく全体を `hidden` で消す
              (= 描画フル画面モード). */}
          <div
            ref={detachedChatRootRef}
            className={
              uiHidden
                ? 'hidden'
                : chatDetached
                  ? `fixed z-40 flex flex-col bg-[var(--color-panel)] text-white ${outsetFrame}`
                  : 'col-span-2 flex min-h-0 min-w-0'
            }
            // 2026-05-27 省スペース化: docked 時は上部バー row 新設で chat は row 3
            // (= canvas の下) に明示配置. detached 時は position: fixed なので
            // grid 配置は無関係.
            // 2026-05-31 chat-scale-cleanup: 旧「チャット欄の大きさ」(= bottom row
            // wrapper 全体 zoom) を撤去. 行高 / 列幅は既存 splitter (chatRowPx /
            // memberColPx の drag) でユーザが直接調整するためそちらに一本化.
            // チャット**文字**サイズだけ別軸 chatFontScale → ChatPanel `fontScaleEm`
            // prop で扱う. detached 時の left/top も raw px そのまま (= zoom 補正
            // 不要、popup 位置が素直な視覚位置と一致).
            style={
              chatDetached
                ? {
                    left: detachedChatPos.x,
                    top: detachedChatPos.y,
                    width: detachedChatSize.width,
                    height: detachedChatSize.height,
                  }
                : { gridRow: 3 }
            }
            {...(chatDetached
              ? ({ role: 'dialog', 'aria-label': 'チャット (切り離し中)' } as const)
              : {})}
          >
            {chatDetached && (
              // タイトルバー = drag handle + 閉じる ✕ ボタン.
              // S3 スライス2 (floating-panels-2026-08-01.md §7): drag 移動は
              // 共有 usePanelDragGrip (= 他 4 popup / toolFloat と同じ配管).
              // ペンタブ対策: `touchAction: 'none'` で OS の pan / scroll ジェスチャ
              // 解釈を抑止する (= 解釈されると pointercancel が飛んで drag が途切れる、
              // LayerPopup / PreviewOverlay / ColorWheelPalette / 発言小窓と同じ修正).
              <div
                className="flex shrink-0 cursor-move select-none items-center justify-between border-b border-black bg-[var(--color-bg-soft)] px-2 py-1 text-[10px] font-bold text-white"
                style={{ touchAction: 'none' }}
                onPointerDown={chatDetachGrip.onPointerDown}
                onPointerMove={chatDetachGrip.onPointerMove}
                onPointerUp={chatDetachGrip.onPointerUp}
                onPointerCancel={chatDetachGrip.onPointerCancel}
                onLostPointerCapture={chatDetachGrip.onLostPointerCapture}
              >
                <span>チャット</span>
                <button
                  type="button"
                  aria-label="チャットを元の位置に戻す"
                  // pointerdown が親 (= title bar) に bubble すると setPointerCapture
                  // が走って × ボタンへの click event が dispatch されなくなる
                  // (= 発言小窓 popup の close ボタンと同じ落とし穴, consult 2026-05-07
                  // バグ修正). 親の drag を始動させないため stopPropagation する.
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => setChatDetached(false)}
                  className="border border-black bg-white px-2 py-0 text-[10px] font-bold text-black"
                >
                  ✕
                </button>
              </div>
            )}
            {/* Chat + Member 横並び. 安定 key で title bar 有無による位置ずれ
                remount を回避. docked では grid p-2 が外側で padding を提供する
                ので内側 p-2 は detached のみ. */}
            <div
              key="chat-member-row"
              ref={chatMemberRowRef}
              className={
                chatDetached
                  ? 'relative flex min-h-0 min-w-0 flex-1 gap-2 p-2'
                  : 'relative flex min-h-0 min-w-0 flex-1 gap-2'
              }
            >
              <div className={`min-h-0 min-w-0 flex-1 ${outsetFrame}`}>
                <ChatPanel
                  ref={chatPanelRef}
                  chat={view.chat}
                  system={view.system}
                  selfUid={state.uid}
                  disabled={state.isRom}
                  onSend={sendChat}
                  // 2026-05-06 仕様統一: chat の filter は SessionProvider 側で
                  // future-only gate されるので, ChatPanel に filteredUids を
                  // 渡す必要はもう無い (drawing engine と同方針).
                  // 2026-05-27 イマドキスクロール挙動 (consult 済): スクロール追従の
                  // 制御は ChatPanel 内部のスクロール位置判定に閉じたので、旧
                  // `scrollLocked` prop / ToolPanel の「あスクロール」toggle は撤去.
                  // 2026-05-31 chat-scale-cleanup: 旧「チャット欄の大きさ」(= 親
                  // wrapper zoom) 撤去に伴い popupZoom prop は不要 (= 発言小窓も
                  // 等倍). 代わりに「チャット文字サイズ」軸を fontScaleEm で渡す
                  // (= log + 入力欄 + 発言小窓 input の文字だけ拡縮).
                  fontScaleEm={chatFontEm}
                  slashPorts={{
                    pushSystemLine,
                    setRom,
                    currentIsRom: state.isRom,
                    clearChatLog,
                    // Phase 7 Task 7-9: `/imagepaste` 経路. `Perm.IMAGEPASTE`
                    // 未保有なら null (= /imagepaste で warn line を出す).
                    // useImagepaste.start を直接渡しているので、ChatPanel 内の
                    // hidden <input> から流れてきた File を engine preview に
                    // 載せるところまで一気通貫で動く.
                    imagepasteOnFile: canImagepaste
                      ? (file): void => {
                          void imagepaste.start(file);
                        }
                      : null,
                    // Phase 6.7 サブ task 後追い (consult 2026-05-07): /bellon roomin.
                    currentBellOnRoomin: bellOnRoomin,
                    setBellOnRoomin,
                    // §12 (floating-panels-2026-08-01.md): /open tool /close tool
                    // /open chat /close chat (旧 Java 版パリティ回復コマンド)。
                    openToolFloat: handleOpenToolFloat,
                    closeToolFloat: handleCloseToolFloat,
                    openChatFloat: handleOpenChatFloat,
                    closeChatFloat: handleCloseChatFloat,
                  }}
                />
              </div>
              {/* Member panel (grid 列幅可変). 旧 Java applet 右下参加者一覧 (room.png) 踏襲.
                  MemberPanel 自体が bg-white を持つので親に背景指定は不要.
                  幅は親 grid の 3 列目 (memberColPx = 物理 px) に従う (= splitter drag-resize).
                  2026-05-31 chat-scale-cleanup: 旧「チャット欄の大きさ」(= 親 bottom
                  row wrapper の `zoom: chatZoom`) 撤去で zoom 倍掛けが消滅. inline
                  width には raw memberColPx をそのまま渡せば grid 列幅と一致する
                  (= 旧 zoom 二重拡大バグも構造的に解消). */}
              <div
                className={`min-h-0 min-w-0 shrink-0 ${outsetFrame}`}
                style={{ width: `${memberColPx}px` }}
              >
                <MemberPanel
                  members={state.members}
                  selfUid={state.uid}
                  selfPermissions={state.permissions}
                  // Phase 7 Task 7-9: kick 実行 callback. MemberPanel が
                  // `window.confirm` を出したあと OK だった場合のみ呼ぶ.
                  // server reject (`admin.kick-rejected`) は reducer 側で
                  // system 行に出るので追加 UI 反映は不要.
                  onKick={({ uid, displayName }) =>
                    sendKick({ targetUid: uid, reason: `kick from member list (${displayName})` })
                  }
                  // Phase 7 Task 7-10b: web ローカルフィルタ. drawing engine と
                  // chat の両方に同じ Set が流れる (Provider が単一 state を所有).
                  filteredUids={filteredUids}
                  onToggleFilter={toggleFilter}
                  // Phase 6.7 サブ task 後追い (consult 2026-05-07): ダブルクリックで
                  // 名前を chat input に挿入. ChatPanelHandle.insertName 経由.
                  onMemberDoubleClick={handleMemberDoubleClick}
                  // 2026-05-17 旧版 `■→` ボタン復活: MemberPanel header 右隣に配置.
                  chatDetached={chatDetached}
                  onToggleChatDetach={() => setChatDetached((p) => !p)}
                  // 2026-05-31 alpha-feedback §3 follow-up: chat と同じ fontScale を
                  // 参加者リストにも適用 (= bottom row の social 系 panel は文字スケール
                  // 揃えないと違和感).
                  fontScaleEm={chatFontEm}
                  // custom-cursor-2026-09-19.md §2.5-3: 編集中カーソルの transient state.
                  cursorDrafts={view.cursorDrafts}
                  // custom-cursor-ux フォローアップ (2026-09-19): 自分の行を主導線化.
                  onOpenCursorEditor={openCursorEditor}
                  showCursorEditorHint={!cursorHintSeen}
                />
              </div>
              {/* 縦 splitter: chat-member-row 内、chat 列 / member 列の gap に重ねる.
                  docked / detached どちらでも親が chat-member-row なので機能する
                  (= 旧版は grid 親 anchor で detached 時に外れていた).
                  2026-05-31 chat-scale-cleanup: 親 wrapper の zoom が撤去された
                  ので boundingRect の物理 px = raw px がそのまま使える (= zoom
                  補正不要、素直な実装に戻る).
                  ガードは `!uiHidden` のみ (横 splitter は据え置きで `!chatDetached`
                  も付く). */}
              {!uiHidden && (
                /* biome-ignore lint/a11y/useSemanticElements: drag-resize splitter は role="separator" 慣用 (hairline 子要素を含むため div). */
                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="参加者リスト列の幅を変更"
                  aria-valuenow={Math.round(memberColPx)}
                  aria-valuemin={MEMBER_COL_PX_MIN}
                  aria-valuemax={MEMBER_COL_PX_MAX}
                  tabIndex={0}
                  data-testid="grid-splitter-col"
                  className="group absolute top-0 bottom-0 z-10 cursor-col-resize select-none"
                  style={{
                    // chat-member-row 内 relative anchor. 右端から memberColPx (raw)
                    // 内側に置く. 2026-05-31 chat-scale-cleanup: 親 wrapper の
                    // zoom 撤去で raw px = 物理 px の素直な対応に戻った.
                    right: `${memberColPx}px`,
                    width: '8px',
                    touchAction: 'none',
                  }}
                  onPointerDown={(e) => {
                    const row = chatMemberRowRef.current;
                    if (row === null) return;
                    e.currentTarget.setPointerCapture(e.pointerId);
                    // 2026-05-31 chat-scale-cleanup: 親 wrapper zoom 撤去で
                    // boundingRect / clientX は同じ物理 px 系. 差をそのまま raw
                    // memberColPx として保存できる (= zoom 補正不要).
                    const rowRightX = row.getBoundingClientRect().right;
                    const move = (ev: PointerEvent): void => {
                      const next = rowRightX - ev.clientX;
                      setMemberColPx(clampMemberColPx(next));
                    };
                    const up = (ev: PointerEvent): void => {
                      try {
                        (ev.target as Element | null)?.releasePointerCapture?.(ev.pointerId);
                      } catch {
                        /* defensive */
                      }
                      window.removeEventListener('pointermove', move);
                      window.removeEventListener('pointerup', up);
                      window.removeEventListener('pointercancel', up);
                    };
                    window.addEventListener('pointermove', move);
                    window.addEventListener('pointerup', up);
                    window.addEventListener('pointercancel', up);
                  }}
                >
                  <div
                    aria-hidden="true"
                    className="pointer-events-none my-auto h-full w-px translate-x-[3px] bg-[var(--color-border)]/0 transition-colors group-hover:bg-[var(--color-border)]/40 group-active:bg-[var(--color-border)]"
                  />
                </div>
              )}
            </div>
            {/* 枠ドラッグ (Windows 風 8 方向) リサイズゾーン (chat-popup-edge-resize).
                2026-05-17 の右下 ◢ ハンドル 1 個を撤去し、PreviewOverlay と同じ
                「パネルの縁に乗る透明判定 div を 8 個 (4 辺 + 4 隅)」へ移行.
                - 見えるグリップは出さず、ホバーで各方向の resize カーソルだけ出す.
                - title bar drag-move / チャット入力とは別 DOM + `stopPropagation`/
                  `preventDefault` で当たり判定を分離 (= n/ne/nw は title bar と
                  重なるが、これらを title bar より手前 (z 高) に置くことで
                  「最上端の細い帯 = リサイズ、その下のバー本体 = 移動」になる).
                - プレビュー窓 (アスペクト固定 edge スカラ) と違い、チャット窓は
                  width/height 独立の自由 2D. 掴んだ辺/隅の反対側 (対辺/対角) を
                  固定点にして `nextFloatingResize` が size + pos を同時更新.
                  確定時 (up/cancel) に画面内へ再 clamp (= サイズは永続化しない,
                  detach 直後の見た目にしか影響しないため従来踏襲). */}
            {chatDetached &&
              DETACHED_CHAT_ZONES.map(({ zone, cursor, z, style }) => (
                <div
                  key={zone}
                  aria-hidden="true"
                  title="ドラッグでチャットウィンドウのサイズを変更"
                  className="absolute"
                  style={{ ...style, zIndex: z, cursor, touchAction: 'none' }}
                  onPointerDown={(e) => {
                    if (e.button !== 0) return;
                    // title bar drag-move / チャット入力と干渉させない.
                    e.stopPropagation();
                    e.preventDefault();
                    const measured = measureDetachedChat();
                    detachedChatResizeAnchorRef.current = {
                      zone,
                      anchor: {
                        originPos: detachedChatPos,
                        originSize: measured,
                        pointerStart: { x: e.clientX, y: e.clientY },
                      },
                    };
                    // ゾーン外へドラッグが出ても追従させる (= OS ウィンドウ枠と同様).
                    e.currentTarget.setPointerCapture(e.pointerId);
                  }}
                  onPointerMove={(e) => {
                    const cur = detachedChatResizeAnchorRef.current;
                    if (cur === null) return;
                    e.stopPropagation();
                    // 親 wrapper zoom 撤去済 (chat-scale-cleanup) なので
                    // pointer delta = visual delta = raw px delta の素直な対応.
                    const next = nextFloatingResize(cur.zone, cur.anchor, {
                      x: e.clientX,
                      y: e.clientY,
                    });
                    setDetachedChatSize({ width: next.width, height: next.height });
                    setDetachedChatPos({ x: next.x, y: next.y });
                  }}
                  onPointerUp={(e) => {
                    if (detachedChatResizeAnchorRef.current === null) return;
                    e.stopPropagation();
                    detachedChatResizeAnchorRef.current = null;
                    try {
                      e.currentTarget.releasePointerCapture(e.pointerId);
                    } catch {
                      // 既に release 済の defensive (Safari 等で起きうる).
                    }
                    clampDetachedChatIntoView();
                  }}
                  onPointerCancel={(e) => {
                    if (detachedChatResizeAnchorRef.current === null) return;
                    e.stopPropagation();
                    detachedChatResizeAnchorRef.current = null;
                    try {
                      e.currentTarget.releasePointerCapture(e.pointerId);
                    } catch {
                      // defensive.
                    }
                    clampDetachedChatIntoView();
                  }}
                />
              ))}
          </div>

          {/* 2026-05-17 追補 (個人 UI スケールの続き) — grid 境界 drag-resize splitter.
              - 横 splitter: canvas 行 / chat 行の境目 (= gap 8px 中) を水平に占める.
                drag で chat 行高 (= raw px) を変える.
              - 縦 splitter: bottom row 内、chat 列 / member 列の境目 (= gap 8px 中)
                を垂直に占める. drag で member 列幅 (= raw px) を変える.
              いずれも grid 親の inner content (= padding 8px を除いた領域) の
              bottom-right を基準にして clientX/Y → raw px に換算する.
              clamp は clampChatRowPx / clampMemberColPx でリアルタイムに掛ける.
              localStorage 書き込みは setChatRowPx / setMemberColPx の 1 経路に
              集約 (= drag 中も immediate persist).
              uiHidden / chatDetached 時は bottom row 自体が消えるので非表示.
              キーボード操作 (= focus + 矢印キーで ±10px) は MVP 未実装 (= role
              "separator" + aria-valuenow まで付けて後付け可能). */}
          {!uiHidden && !chatDetached && (
            <>
              {/* 横 splitter: 全幅 8px, canvas 行と chat 行の gap に重ねる.
                  `role="separator"` は ARIA の resize splitter 慣用パターン
                  (= MDN "WAI-ARIA: separator role"). `<hr>` は void element で
                  inner hairline 子を持てない (= hover/active highlight 用) ため
                  div + role を採用.
                  横 splitter は grid 親 (gridParentRef) 直下に置く. chatDetached
                  時は bottom row 自体が floating frame に出るので、grid 親 anchor
                  の本 splitter は意味を成さない → ガード `!uiHidden && !chatDetached`
                  で非表示. (縦 splitter は chat-member-row 内に移したので docked /
                  detached どちらでも機能する.) */}
              {/* biome-ignore lint/a11y/useSemanticElements: drag-resize splitter は role="separator" 慣用 (hairline 子要素を含むため div). */}
              <div
                role="separator"
                aria-orientation="horizontal"
                aria-label="チャット行の高さを変更"
                aria-valuenow={Math.round(chatRowPx)}
                aria-valuemin={CHAT_ROW_PX_MIN}
                aria-valuemax={CHAT_ROW_PX_MAX}
                // tabIndex=0 で focus 可 (= 矢印キーで ±10px 調整は post-MVP、
                // 仕様より仕込みだけ先行). 現状は focus indicator は出るが key
                // listener は付けていない.
                tabIndex={0}
                data-testid="grid-splitter-row"
                // 2026-08-01 追加依頼: 右ドック時はサイドバーが右端に来るため、
                // splitter の固定辺 (8px) を左に、可変辺 (サイドバーを避ける方)
                // を右に入れ替える (下の style の left/right 分岐と対になる)。
                className={`group absolute z-10 cursor-row-resize select-none ${
                  sidebarDockSide === 'right' ? 'left-2' : 'right-2'
                }`}
                style={{
                  // bottom of splitter = padding-bottom + bottom-row-height
                  // (= 8 + chatRowPx). 8px hit area が gap 全体を覆う.
                  // 2026-05-31 chat-scale-cleanup: 旧 zoom 倍掛け (= bottom row
                  // wrapper の親 zoom) は撤去されたので raw chatRowPx をそのまま使う.
                  bottom: `${8 + chatRowPx}px`,
                  height: '8px',
                  touchAction: 'none',
                  // 2026-05-31 stage-Y regression fix: splitter は機能的に chat 行
                  // (= 中央 + 右カラム) の上端でだけ drag できれば良いのに、旧実装
                  // (= `left-2 right-2`) は grid 全幅 (= 左カラム ToolPanel の上まで)
                  // を覆っていた. Stage Y で `ENABLE_MARKER_PEN=false` により ToolPanel
                  // の縦寸が縮み、最下段 `[レイヤー]` toggle button の y 位置が
                  // splitter band (z-10 absolute) と重なって click が intercept される
                  // regression を踏んだ (e2e `layerPopupVisibilityToggle.spec.ts` b/c/d
                  // が fail). 左カラム上から splitter を除外するため left を動的に計算.
                  // 2026-06-13 越境 fix (classic-sidebar-compaction.md §8): 左カラム列
                  // が minmax(60 * sidebarZoom px, max-content) で中身追従になり定数式
                  // (16 + 60 * sidebarZoom) では列右端を仮定できないため、splitterLeftPx
                  // (= 左カラム wrapper の実測 right + gap 8px、ResizeObserver +
                  // useLayoutEffect で追従) を使う. Math.max の第 2 項は実測前 (null)
                  // / 異常値の floor (= 旧定数式 = 列の下限幅由来の最小 left).
                  // chatDetached 時は bottom row 自体が消え、本 splitter は描画されない
                  // ので考慮不要. uiHidden 時も同様 (`!uiHidden && !chatDetached`
                  // gate 内). §12: サイドバーがフロート中は列自体が無い (= floor は
                  // 0、splitter は grid 左端から始まってよい)。2026-08-01 追加依頼:
                  // 右ドック時は避けるべき辺が右側になるため `right` を動的計算に
                  // 差し替える (`splitterLeftPx` は effect 側で既に「サイドバーの
                  // ある側からの inset」に汎化済み、ここは出し分けるだけ)。
                  ...(sidebarDockSide === 'right'
                    ? {
                        right: `${Math.max(splitterLeftPx ?? 0, sidebarFloatOpen ? 0 : 16 + 60 * sidebarZoom)}px`,
                      }
                    : {
                        left: `${Math.max(splitterLeftPx ?? 0, sidebarFloatOpen ? 0 : 16 + 60 * sidebarZoom)}px`,
                      }),
                }}
                onPointerDown={(e) => {
                  const parent = gridParentRef.current;
                  if (parent === null) return;
                  e.currentTarget.setPointerCapture(e.pointerId);
                  // grid 親 inner-bottom (padding-bottom 8px を除いた値) を anchor に.
                  const gridBottomY = parent.getBoundingClientRect().bottom - 8;
                  const move = (ev: PointerEvent): void => {
                    // 2026-05-31 chat-scale-cleanup: bottom row wrapper zoom 撤去で
                    // pointer delta = raw chatRowPx delta. zoom 補正不要.
                    const next = gridBottomY - ev.clientY;
                    setChatRowPx(clampChatRowPx(next));
                  };
                  const up = (ev: PointerEvent): void => {
                    try {
                      (ev.target as Element | null)?.releasePointerCapture?.(ev.pointerId);
                    } catch {
                      /* defensive */
                    }
                    window.removeEventListener('pointermove', move);
                    window.removeEventListener('pointerup', up);
                    window.removeEventListener('pointercancel', up);
                  };
                  window.addEventListener('pointermove', move);
                  window.addEventListener('pointerup', up);
                  window.addEventListener('pointercancel', up);
                }}
              >
                {/* hover/active で薄いハイライト線. hit area (8px) の中央に 1px 線. */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none mx-auto h-px w-full translate-y-[3px] bg-[var(--color-border)]/0 transition-colors group-hover:bg-[var(--color-border)]/40 group-active:bg-[var(--color-border)]"
                />
              </div>
            </>
          )}
        </div>

        {/* W3 Classic レイヤー追加 (consult 2026-05-26, docs/_wip/w3-classic-layers.md):
            常駐 floating レイヤー popup. ヘッダを掴んでドラッグ移動だけできる.
            **Tab UI hide に連動** させるため `!uiHidden` gate で他 chrome と一緒に
            消す. 2 操作 (選択 / 👁) をそれぞれ PenConfig.layerId 更新 /
            engine.setLayerVisible に配線する (per-layer clear 🗑 は 2026-05-26 撤去).

            2026-05-31 layer-popup-visibility-toggle (`w3-classic-layers.md §2.2.1`
            + §2.2.2 post-implementation 調整):
            `layerPopupVisible` (= ToolPanel の Preview button 直下 `[レイヤー]`
            toggle 駆動の localStorage 永続化 state) を AND 条件に追加. LayerPopup
            自身には ✕ ボタンを付けず (= §2.2 「閉じるボタンなし」精神維持)、戻し方は
            ToolPanel 内 toggle 一本. §2.2.1 初版では ArgbSliderPanel 内に統合
            していたが §2.2.2 で Preview toggle と並べて ToolPanel に移動.
            visibility map は ClassicLayout 側で持つので popup hide → 再表示でも
            保持される (= engine state とも整合). */}
        {!uiHidden && layerPopupVisible && (
          <LayerPopup
            activeLayerId={pen.layerId}
            visibility={layerVisibilityMap}
            onSelectActive={handleSelectActiveLayer}
            onToggleVisible={handleToggleLayerVisible}
            backgroundVisible={backgroundVisible}
            onToggleBackgroundVisible={handleToggleBackgroundVisible}
            backgroundRgb={backgroundRgb}
            canSetBackground={canSetBackground}
            onToggleBackgroundColorWheel={() => setBgColorWheelOpen((o) => !o)}
            dockSide={sidebarDockSide}
            opacities={layerOpacities}
            opacityControl={layerOpacityControl}
            thumbnails={layerThumbnailHub}
            regionRoles={chrome.region.roles}
          />
        )}

        {/* 2026-06-20 ペン種選択をフロート「ペン」に集約 (consult 2026-06-20、A 採択).
            [ペン] 入口ボタン (ToolPanel) が開閉する常設フロート「ペン」(PenPopup).
            LayerPopup / PreviewOverlay と同型 (= `!uiHidden` gate で Tab UI hide に連動、
            ヘッダ drag 移動、位置 localStorage 記憶、可視 state も localStorage 永続化)。
            中身は Modern と共有する PenKindPicker (ペン種チップ + texture preset)。
            - ペン種選択 (`onSelectPen`) は共有 chrome に直結 (= `pen=kind`、Modern と同経路)。
            - texture preset 選択 (`handleSelectTexturePreset`) は pen='texture' 切替 +
              preset セット (= 共有 chrome 経路、engine 変更なし)。 */}
        {!uiHidden && penPopupOpen && (
          <PenPopup
            pen={pen.pen}
            onSelectPen={onSelectPen}
            texturePreset={texturePreset}
            onSelectTexturePreset={handleSelectTexturePreset}
            airbrushGrain={airbrushGrain}
            onAirbrushGrainChange={onAirbrushGrainChange}
            airbrushDensity={airbrushDensity}
            onAirbrushDensityChange={onAirbrushDensityChange}
            crayonGrain={crayonGrain}
            onCrayonGrainChange={onCrayonGrainChange}
            pastelGrain={pastelGrain}
            onPastelGrainChange={onPastelGrainChange}
            toneDensity={toneDensity}
            onToneDensityChange={onToneDensityChange}
            tonePitch={tonePitch}
            onTonePitchChange={onTonePitchChange}
            dotSpacing={dotSpacing}
            onDotSpacingChange={onDotSpacingChange}
            dockSide={sidebarDockSide}
          />
        )}

        {/* custom-cursor-2026-09-19.md §2.5: 「カーソル」フロート (24×24 ドット絵
            エディタ)。ToolPanel の [カーソル] 入口ボタンが開閉する。位置は
            PenPopup と同じ仕組み (`CursorEditorPopup` 内で localStorage 記憶)、
            開閉は ephemeral (= 永続化しない、`cursorEditorOpen` local state)。 */}
        {!uiHidden && cursorEditorOpen && (
          <CursorEditorPopup
            onClose={() => setCursorEditorOpen(false)}
            initialCursor={selfCursor}
            currentColor={{ rgb24: pen.color, alpha: pen.alpha }}
            spuitMode={chrome.spuitMode}
            onSpuitPick={({ rgb24, alpha }) => {
              onColorChange(rgb24);
              onAlphaChange(alpha);
            }}
            onDraft={sendCursorDraft}
            onConfirm={sendCursorChange}
            onRemove={() => sendCursorChange(null)}
            dockSide={sidebarDockSide}
            // 作者 staging フィードバック 2026-09-19 follow-up: 自分の画面での表示を
            // 個人設定で止められる (PenSettingsPanel と同じ共有 chrome 値)。
            showOwnCustomCursor={chrome.showOwnCustomCursor}
            onShowOwnCustomCursorChange={chrome.onShowOwnCustomCursorChange}
          />
        )}
      </div>

      {settingsOpen && (
        <RoomSettingsModal
          roomId={state.roomId}
          roomName={state.roomName}
          // Phase 7 Task 7-5: clearOpen の初期値を渡す. RoomState は room.welcome
          // で受け取った値を保持しているのでそのまま転載.
          clearOpen={state.clearOpen}
          // 2026-05-27 上部バー再編: close 経路で owner 判定を再評価 (= モーダル内で
          // ログインして token を取得した直後にボタンを認証済表示へ更新する).
          onClose={closeSettings}
        />
      )}

      {/* 2026-05-21: 全消し確認モーダル (旧 `window.confirm`).
          iPad Safari 全画面モード解除を避けるための置換. destructive=true で
          赤系 OK ボタンに. */}
      <WhatsNewDialog
        open={whatsNew.open}
        onClose={whatsNew.closeDialog}
        entries={whatsNew.entries}
        newIds={whatsNew.newIds}
      />
      <InviteDialog
        open={inviteDialog.open}
        onClose={inviteDialog.closeDialog}
        url={buildRoomInviteUrl(window.location.origin, state.roomId)}
        roomLabel={state.roomName || state.roomId.slice(0, 8)}
        entryPasswordRequired={inviteDialog.entryPasswordRequired}
        onAction={inviteDialog.report}
      />
      <ConfirmDialog
        open={clearConfirmOpen}
        title="キャンバスを全消し"
        message="キャンバスを全消ししますか?"
        confirmLabel="全消し"
        destructive
        onConfirm={() => {
          sendClear();
          setClearConfirmOpen(false);
        }}
        onCancel={() => setClearConfirmOpen(false)}
      />

      {/* 2026-06-02 iPad 全画面注意モーダル (consult 2026-06-02): iOS/iPadOS で
          全画面ボタンを押して「全画面に入ろうとした」とき、まだ一度も全画面に
          入っていない間は毎回出す (入ったら以後 session 内は出さない).
          ネイティブ alert/confirm は iPad で全画面が解除される罠があるため
          ConfirmDialog を再利用. 「全画面にする」(onConfirm) で実際に
          requestFullscreen、「やめておく」(onCancel・既定 focus) で何もしない. */}
      <ConfirmDialog
        open={fullscreenWarnOpen}
        title="全画面について"
        message="iPad では仕様上、画面を下にスワイプすると全画面が解除されます。全画面にせず、そのままお使いいただくのを推奨します。"
        confirmLabel="全画面にする"
        cancelLabel="やめておく"
        onConfirm={() => {
          setFullscreenWarnOpen(false);
          enterFullscreen();
        }}
        onCancel={() => setFullscreenWarnOpen(false)}
      />

      {/* 2026-05-17 個人 UI スケール設定 (consult 2026-05-17): 「表示」ボタンから
          開く. RoomSettingsModal とは別で owner 権限不要、全員が開ける.
          選択 → setSidebarScale / setChatFontScale 経由で即時 localStorage に永続化.
          - sidebarScale: 左カラム wrapper の zoom inline style に反映.
          - chatFontScale: ChatPanel `fontScaleEm` prop に流れて log + 入力欄 +
            発言小窓 input の文字サイズだけ拡縮 (= 2026-05-31 chat-scale-cleanup
            で旧「チャット欄の大きさ」軸を置き換え). */}
      {/* 2026-06-13 続報: 旧「ペン詳細」セクションは独立した PenSettingsModal
          (下) へ移動し、本モーダルは表示系設定のみに戻った。 */}
      {uiPrefsOpen && (
        <UserPreferencesModal
          sidebarScale={sidebarScale}
          chatFontScale={chatFontScale}
          onSidebarScaleChange={setSidebarScale}
          onChatFontScaleChange={setChatFontScale}
          layerThumbnailsEnabled={layerThumbnailsEnabled}
          onLayerThumbnailsEnabledChange={onLayerThumbnailsEnabledChange}
          layerThumbnailsStopped={layerThumbnailsStopped}
          onClose={() => setUiPrefsOpen(false)}
          // S3 スライス2 (floating-panels-2026-08-01.md §7 経路3「配置をリセット」):
          // Classic のみ。Modern は自前のワークスペース preset ピッカー
          // (ModernWorkspacePresetPicker) が同役を担うため渡さない (= undefined
          // で「配置をリセット」行自体が出ない, UserPreferencesModal 側の分岐)。
          onResetLayout={handleResetClassicLayout}
        />
      )}

      {/* 2026-06-13 続報 (consult): ペン設定モーダル (手ぶれ補正 / AA、modern-layout.md
          §1.1 表現パリティ)。ヘッダ「ペン」項目から開く (= ショートカット設定と同じ
          独立モーダルの流儀)。ToolPanel は chrome 凍結 (classic-sidebar-compaction.md
          §7) のため設定系モディファイアの受け皿はここ。値は共有 chrome (tri-state
          実効値、共通既定 off) を読み書きするので、未設定なら off/off = 旧 Java 互換の
          描き味のまま。ModernToolProperties と同じ単一 source (= 二重の真実なし)。 */}
      {penSettingsOpen && (
        <PenSettingsModal
          stabilizerLevel={chrome.stabilizerLevel}
          onStabilizerChange={chrome.onStabilizerChange}
          aaEnabled={chrome.aaEnabled}
          onAaChange={chrome.onAaChange}
          dotByDotMode={chrome.dotByDotMode}
          onDotByDotModeChange={chrome.onDotByDotModeChange}
          dotByDotActive={chrome.dotByDotActive}
          // 2026-06-27 ブラシカーソル / スポイトの取得元 (共有 chrome): 旧「表示」モーダル
          // から本モーダルへ移設。Classic/Modern 共通の値。
          brushCursorType={chrome.brushCursorType}
          onBrushCursorTypeChange={chrome.onBrushCursorTypeChange}
          // 作者 staging フィードバック 2026-09-19 follow-up: 自分のカーソルを自分の
          // 画面にも表示するか (共有 chrome、CursorEditorPanel 下部と同じ値)。
          showOwnCustomCursor={chrome.showOwnCustomCursor}
          onShowOwnCustomCursorChange={chrome.onShowOwnCustomCursorChange}
          spuitSampleMode={chrome.spuitSampleMode}
          onSpuitSampleModeChange={chrome.onSpuitSampleModeChange}
          onClose={() => setPenSettingsOpen(false)}
        />
      )}

      {/* 2026-06-12 ショートカット設定 (shortcut-settings.md スライス2): ヘッダ
          「ショートカット」項目 + `?` ホットキーから開く独立モーダル。layout 非依存
          (Classic / Modern で同一)。中身は self-contained な ShortcutSettingsPanel。 */}
      {shortcutsOpen && (
        <ShortcutSettingsModal currentLayout="classic" onClose={() => setShortcutsOpen(false)} />
      )}

      {/* T3 絵ログ (タイムラプス) X 共有 (growth-timelapse-share-2026-07-02.md §5.2/§5.3):
          Classic 上部バー「絵ログ」項目から開く。モーダル本体は presentation/ 直下で
          レイアウト非依存 (= Modern と共通) なので Classic からも同一 props で呼べる。
          canvas 寸法は roomMeta (state.canvas) 由来 = replay の原寸 raster 割当に必須
          (T2 必須引数)。close で生成中 abort はモーダル側が担う。Classic はデスクトップ
          専用 (スマホは Modern 固定) なので isMobile は既定 (false) のまま。 */}
      {timelapseOpen && (
        <TimelapseShareModal
          roomId={state.roomId}
          canvasWidth={state.canvas.width}
          canvasHeight={state.canvas.height}
          transport={transport}
          workerFactory={createTimelapseWorker}
          adminToken={adminTokens.get(state.roomId) ?? undefined}
          onClose={() => setTimelapseOpen(false)}
        />
      )}

      {/* 過去キャンバス一覧 (Slice 2)。Classic 上部バー「過去キャンバス」項目から開く。
          モーダル本体は presentation/ 直下でレイアウト非依存 (= Modern と共通)。絵ログ
          共有への橋渡し (過去キャンバスの snapshotSeq を終端に初期化) はモーダル内が担う。
          Classic はデスクトップ専用なので isMobile は既定 (false)。 */}
      {pastCanvasOpen && (
        <PastCanvasListModal
          roomId={state.roomId}
          canvasWidth={state.canvas.width}
          canvasHeight={state.canvas.height}
          transport={transport}
          workerFactory={createTimelapseWorker}
          adminToken={adminTokens.get(state.roomId) ?? undefined}
          permissions={state.permissions}
          getCurrentCanvasBlob={chrome.getCurrentCanvasBlob}
          currentBackgroundRgb={backgroundRgb}
          {...(pastCanvasRecoverSeq !== undefined
            ? { initialRecoverSeq: pastCanvasRecoverSeq }
            : {})}
          onClose={() => {
            setPastCanvasOpen(false);
            setPastCanvasRecoverSeq(undefined);
          }}
        />
      )}

      {/* Phase 6.7 サブ task (consult 2026-05-07 → 修正 → controlled 化): カラー
          サークル floating palette. dblclick した palette slot から開く.
          wheel/slider/numeric/HEX を弄るたびに描画色 + alpha を即更新 (=
          ArgbSliderPanel と同経路、OK ボタン廃止). controlled: `value` に現在
          の pen.color / pen.alpha を流すことで, palette slot を別 slot に
          切替えても panel 内 wheel が追従する. 閉じる経路は × ボタン + ESC
          のみ (描画と並行操作可、Clip Studio / Photoshop 流儀). */}
      {colorWheelOpen && (
        <ColorWheelPalette
          value={{ rgb24: pen.color, alpha255: pen.alpha }}
          onColorChange={onColorChange}
          onAlphaChange={onAlphaChange}
          onClose={() => setColorWheelOpen(false)}
        />
      )}

      {/* A-4 背景色 ■ swatch (canvas-background-color §4.3): 背景色 ColorWheelPalette。
          draw 色 palette とは別 open-state (bgColorWheelOpen)。LayerPopup の ■ から開く。
          value = 現在 bg (seed)、onColorChange = handleBackgroundColorChange (= 共有
          chrome が即ローカルプレビュー + trailing debounce send)。bg alpha は 255 固定なので
          onAlphaChange は no-op (payload に alpha 無し)。perm が無くなったら閉じる。 */}
      {canSetBackground && bgColorWheelOpen && (
        <ColorWheelPalette
          value={{ rgb24: backgroundRgb, alpha255: 255 }}
          onColorChange={handleBackgroundColorChange}
          onAlphaChange={() => {
            /* bg alpha は 255 固定 (§4.3) */
          }}
          onClose={() => setBgColorWheelOpen(false)}
        />
      )}

      {/* 2026-05-17 旧版 PreviewFrame 復活 (旧 PreviewCanvas.java /
          PreviewFrame.java 踏襲): キャンバス全体プレビュー floating panel.
          canvasRef / canvasScrollRef / canvas 寸法 / zoom を渡すと、内部で
          requestAnimationFrame ループで毎 frame 縮小描画 + 表示範囲枠更新.
          クリック/ドラッグでクリック点を中心にしたスクロール位置へ移動. */}
      {previewOpen && (
        <PreviewOverlay
          canvasRef={canvasRef}
          wrapperRef={canvasScrollRef}
          // 2026-09-29 viewport transform 移行 (決定5): pan 位置は chrome
          // (useRoomChrome) が single source of truth で持つ `{tx,ty}`。
          viewMapper={chrome.viewMapper}
          onSetViewport={chrome.setViewport}
          canvasWidth={state.canvas.width}
          canvasHeight={state.canvas.height}
          zoom={displayScale}
          flipX={flipX}
          onClose={() => setPreviewOpen(false)}
          initialEdge={previewSizeStoreRef.current.get()}
          onResizeCommit={(edge) => previewSizeStoreRef.current.set(edge)}
          // 2026-06-13 (feature/preview-perf): 自分のストローク中 / パン中はサムネ再生成を
          // サスペンド (= カーソルのフレーム予算を守る)。isStrokeActive は chrome の ref
          // getter、isPanning は Space hold / トグルの OR。表示範囲枠だけは分離して追従する。
          isSuspended={() => isLocalInteractionActive(isStrokeActive(), isPanning)}
          // 2026-09-28 (feature/preview-panel-background): サムネにも部屋の背景色を反映
          // (DrawingCanvas と同じ chrome state)。
          backgroundVisible={backgroundVisible}
          backgroundRgb={backgroundRgb}
        />
      )}

      {/* §9.7 Phase 2: imagepaste preview の Confirm/Cancel overlay は CanvasStage 側へ
          移設 (canvas と一緒に永続化)。imagepaste state 自体は共有コントローラ (chrome) が
          所有し、ChatPanel / ToolPanel の /imagepaste 起動 (chrome.imagepaste.start) で動く。
          2026-06-07 view flip (canvas-flip.md §5) の preview rect X 再マップ用 flipX も
          CanvasStage が chrome.flipX を読んで ImagepasteOverlay へ渡す。 */}

      {/* conflict-aware Undo の拒否トースト (2026-07-23 是正、modern-layout.md §1.1
          表現パリティ): reason 別の文言をキャンバス中央に約 3 秒表示する。
          ModernLayout と同一の ImagepasteToastOverlay を直接描画する (= 両シェル共通、
          CanvasStage を介さない)。 */}
      <ImagepasteToastOverlay
        viewMapper={chrome.viewMapper}
        toast={undo.rejectToast}
        onDismiss={undo.dismissRejectToast}
        autoDismissMs={UNDO_REJECT_DISPLAY_MS}
      />

      {/* conflict-aware Undo の bbox ハイライト (2026-07-23 是正): `reason='conflict'`
          の交差 stroke をキャンバス上に一時的に赤枠で示す。ModernLayout と同一の
          viewMapper 座標変換方式 (zoom / pan / flip / 回転は viewport.ts §1b の変換行列が
          吸収)。キャンバス本体のピクセルには一切書き込まない。 */}
      <UndoConflictHighlightOverlay viewMapper={chrome.viewMapper} boxes={undo.highlightBoxes} />

      {/* 2026-05-22 タカミン窓口 (= フィードバック UI、launch-rollout-plan.md §3.3 /
          §3.3.7). 画面右下にフローティング配置. W2 公開 (2026-05-23 21:00) と同時投入.
          MVP: 静的アイコン + モーダルフォーム + WS feedback.submit + ack/rejected 表示.
          アニメーション (200ms 切替) / コンテキスト自動添付 / admin UI は後追い投入. */}
      <FeedbackButton />
    </>
  );
};

// 2026-06-12: TopBar / TopBarButton は `../shared/RoomHeader.tsx` (RoomHeader /
// RoomHeaderButton) へ抽出した。Classic / Modern が同一の presentational component
// を共有することで帯の見た目 (padding / gap / font / ボタン / 高さ / ルーム名左寄せ)
// のドリフトを構造的に止める。Classic はここで <RoomHeader items={topBarItems} ...>
// として呼ぶだけ (= 項目データ topBarItems の組み立ては上の render 部に残る)。
